import { parseReimbursementBatch } from "./reimbursement-batch";
import { applyWeekToFund } from "./week-to-fund";
import { previewBankCredit, commitBankCredit, type BankCreditInput } from "./bank-credit-store";
import { previewCardPaymentTiming, commitCardPaymentTiming } from "./card-payment-timing-store";
import { creditForCut } from "./bank-credits";
import { paymentTimingForCut, type CardPaymentTimingInput } from "./card-payment-timing";
import { buildCardStatementSummary } from "./card-statement";
import type { User } from "firebase/auth";
import { cardPeriodForDate, isMovementInCurrentPeriod, isTrackedCard } from "./finance";
import { allocateTemporaryContributions, applyPendingPeriodFunding, applyPreviousPeriodCarryover, applySavingsWeekTransfer, applyTemporaryContributionChange, applyTemporaryContributionDestination, applyWeekFunding, assertAllocationsWithinNu, carryoverAtPeriodClose, isPeriodReadyToClose, mexicoCityIsoDate, periodEndForStart, snapshotForDate, splitPeriodBudgetInHalves, splitPeriodWeekRanges } from "./periods";
import { assertCutConfirmedForPayment, applyCardPaymentToSnapshot, cardCutStates, cardPaymentEventId, cardPaymentMovementIds, cardPaymentRequirement, includeInheritedCardCut, settleCardPeriodMovements } from "./card-payments";
import { applyPendingFundingChange, applyReimbursementFundingChange, creditCardReimbursements, pendingBreakdown, type PendingFundingSource, type ReimbursementFundingSource } from "./pending";
import { editedAccountDeltaCents, planMovementEdit } from "./movement-edit";
import { commitMovementCorrection, previewMovementCorrection, readCorrectionDetail } from "./movement-correction-store";
import type { CorrectionInput } from "./movement-correction";
import { applyYieldUpdate, YIELD_FUND_ID, YIELD_FUND_LABEL } from "./yield-update";
import { installmentIsDue } from "./installments";
import { saveBankPosting, type BankPostingInput } from "./bank-posting-store";
import type { ActionJob, ActionJobKind, FinancialSnapshot, Movement, PendingPaymentAllocation, TemporaryContribution, TemporaryContributionDestination, TemporaryContributionEvent, WeekBalance, WeekId } from "./types";

type SnapshotMutationOutcome = Record<string, unknown> & {
  result: Record<string, unknown>;
  snapshot: FinancialSnapshot;
};

const cents = (value: unknown): number => Math.round(Number(value ?? 0) * 100);
const money = (value: number): number => Math.round(value) / 100;
const nowIso = (): string => new Date().toISOString();
const dateOnly = (): string => mexicoCityIsoDate();
const isoDateValue = (date: Date): string => [date.getFullYear(), String(date.getMonth() + 1).padStart(2, "0"), String(date.getDate()).padStart(2, "0")].join("-");
const slug = (value: string): string => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const safeDocumentId = (value: unknown, prefix: string): string => {
  const id = String(value ?? "").trim().replaceAll("/", "-");
  return id || `${prefix}_${crypto.randomUUID()}`;
};

function accountWithDelta(snapshot: FinancialSnapshot, deltaCents: number): FinancialSnapshot["accounts"] {
  const balance = snapshot.accounts[0]?.balanceCents ?? 0;
  if (!Number.isSafeInteger(deltaCents) || balance + deltaCents < 0) {
    throw new Error("La operación dejaría el saldo de Nu por debajo de cero.");
  }
  return snapshot.accounts.map((account, index) => index === 0
    ? { ...account, balanceCents: account.balanceCents + deltaCents }
    : account);
}

function activeWeek(snapshot: FinancialSnapshot): WeekBalance {
  const week = snapshot.weeks.find((item) => item.status === "active");
  if (!week) throw new Error("No hay una semana activa. Cierra o crea la quincena correspondiente antes de mover presupuesto.");
  return week;
}

function replaceMovement(snapshot: FinancialSnapshot, movement: Movement): FinancialSnapshot {
  const exists = snapshot.movements.some((item) => item.id === movement.id);
  return {
    ...snapshot,
    movements: (exists
      ? snapshot.movements.map((item) => item.id === movement.id ? movement : item)
      : [movement, ...snapshot.movements]).slice(0, 200),
  };
}

export function recognizeDueInstallment(
  snapshot: FinancialSnapshot,
  movement: Movement,
  today = dateOnly(),
): { snapshot: FinancialSnapshot; movement: Movement } {
  if (!movement.installmentPurchaseId) return { snapshot, movement };
  if (!installmentIsDue(movement, today)) {
    throw new Error("El corte de esta parcialidad todavía no comienza. Podrás apartarla desde el primer día de su corte.");
  }
  const recognizedBefore = Math.min(movement.amountCents, Math.max(0, movement.installmentRecognizedCents ?? 0));
  const recognitionCents = movement.amountCents - recognizedBefore;
  // Fondear o reembolsar después no traslada un gasto ya reconocido de quincena.
  if (recognitionCents === 0) return { snapshot, movement };
  return {
    snapshot: recognitionCents > 0 ? {
      ...snapshot,
      totalSpentCents: snapshot.totalSpentCents + recognitionCents,
      categorySpend: categoryWithDelta(snapshot, movement.category, recognitionCents),
    } : snapshot,
    movement: {
      ...movement,
      periodId: snapshot.periodLabel,
      accountingPeriodId: movement.accountingPeriodId ? snapshot.periodLabel : undefined,
      installmentRecognizedCents: movement.amountCents,
    },
  };
}

function updateCardReserve(snapshot: FinancialSnapshot, card: string, deltaCents: number): FinancialSnapshot["cardPayments"] {
  let found = false;
  const cards = snapshot.cardPayments.map((item) => {
    if (item.name !== card) return item;
    found = true;
    const nextAmountCents = item.amountCents + deltaCents;
    if (nextAmountCents < 0) throw new Error(`La reserva de ${card} no alcanza para aplicar este cambio.`);
    return { ...item, amountCents: nextAmountCents };
  });
  if (!found && deltaCents < 0) throw new Error(`No existe una reserva de ${card} que pueda reducirse.`);
  return found || !deltaCents ? cards : [...cards, { name: card, amountCents: deltaCents }];
}

function categoryWithDelta(
  snapshot: FinancialSnapshot,
  name: string | undefined,
  deltaCents: number,
): FinancialSnapshot["categorySpend"] {
  if (!name || !deltaCents) return snapshot.categorySpend;
  const found = snapshot.categorySpend.some((item) => item.name === name);
  return (found
    ? snapshot.categorySpend.map((item) => item.name === name
      ? { ...item, amountCents: Math.max(0, item.amountCents + deltaCents) }
      : item)
    : [...snapshot.categorySpend, { name, amountCents: Math.max(0, deltaCents), icon: "✦" }])
    .filter((item) => item.amountCents > 0)
    .sort((a, b) => b.amountCents - a.amountCents);
}

function ownCoveredByWeek(movement: Movement): number {
  if (movement.budgetImpact === false || movement.fixedExpense) return 0;
  if (!isTrackedCard(movement.paymentMethod)) return Math.max(0, movement.amountCents);
  if (movement.cardFundingMode === "later") return 0;
  if (movement.cardFundingMode === "partial") return Math.min(movement.amountCents, Math.max(0, movement.amountTodayCents ?? 0));
  return Math.max(0, movement.amountCents);
}

function movementCoveredCents(movement: Movement): number {
  const billed = Math.max(0, movement.billedAmountCents ?? movement.amountCents);
  const reimbursement = movement.reimbursementReceived ? Math.max(0, movement.reimbursementCents ?? 0) : 0;
  const ownCovered = isTrackedCard(movement.paymentMethod)
    ? ownCoveredByWeek(movement)
    : Math.max(0, movement.amountCents);
  return Math.min(billed, ownCovered + reimbursement);
}

function isSettledMovement(movement: Movement): boolean {
  return movement.cardPaymentStatus === "paid" || Boolean(movement.cardPaymentEventId);
}

function hasLinkedSavings(snapshot: FinancialSnapshot, movement: Movement): boolean {
  return Math.max(0, movement.savingsContributionCents ?? 0) > 0
    || Math.max(0, movement.savingsRescueCents ?? 0) > 0
    || snapshot.movements.some((candidate) => candidate.relatedMovementId === movement.id
      && (candidate.kind === "savings_contribution" || candidate.kind === "savings_rescue"));
}

function inferredAccountDelta(movement: Movement): number {
  if (Number.isFinite(movement.accountDeltaCents)) return Number(movement.accountDeltaCents);
  const receivedReimbursement = movement.reimbursementReceived
    && (!movement.reimbursementFundingSource || movement.reimbursementFundingSource === "new")
    ? Math.max(0, movement.reimbursementCents ?? 0)
    : 0;
  return receivedReimbursement
    - (movement.paymentMethod === "Transferencia" ? (movement.billedAmountCents ?? movement.amountCents) : 0);
}

function reimbursementFundingSourceFromMovement(movement: Movement): ReimbursementFundingSource {
  const kind = movement.reimbursementFundingSource ?? "new";
  return {
    kind,
    sourceId: movement.reimbursementFundingSourceId,
    periodId: movement.reimbursementFundingSourcePeriodId,
  };
}

function periodEndFromStart(start: string): string {
  const end = periodEndForStart(start);
  if (!end) throw new Error("La fecha debe ser el inicio exacto de la siguiente quincena.");
  return end;
}

function addIsoDays(value: string, days: number): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) throw new Error("La fecha del periodo actual no es válida.");
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
  if (date.getFullYear() !== Number(match[1]) || date.getMonth() !== Number(match[2]) - 1 || date.getDate() !== Number(match[3])) {
    throw new Error("La fecha del periodo actual no existe en el calendario.");
  }
  date.setDate(date.getDate() + days);
  return isoDateValue(date);
}

function periodLabel(start: string): string {
  const end = periodEndFromStart(start);
  const date = new Date(`${end}T12:00:00`);
  const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  return `${months[date.getMonth()]} Q${date.getDate() <= 15 ? 1 : 2}-${date.getFullYear()}`;
}

function rangeLabel(start: Date, end: Date): string {
  const short = (date: Date) => `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}`;
  return `${short(start)} — ${short(end)}`;
}

async function readSnapshot(uid: string): Promise<FinancialSnapshot> {
  const [{ doc, getDoc }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const document = await getDoc(doc(firestore, "users", uid, "snapshots", "current"));
  if (!document.exists()) throw new Error("No encontré el estado financiero en Firebase.");
  return snapshotForDate({ ...(document.data() as FinancialSnapshot), source: "firestore" });
}

async function mutateSnapshot(
  uid: string,
  mutate: (
    snapshot: FinancialSnapshot,
    transaction: import("firebase/firestore").Transaction,
    document: (collection: string, id: string) => import("firebase/firestore").DocumentReference,
  ) => Promise<{ snapshot: FinancialSnapshot; result?: Record<string, unknown>; skipSnapshotWrite?: boolean }> | { snapshot: FinancialSnapshot; result?: Record<string, unknown>; skipSnapshotWrite?: boolean },
): Promise<SnapshotMutationOutcome> {
  const [{ doc, runTransaction }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  return runTransaction(firestore, async (transaction) => {
    const reference = doc(firestore, "users", uid, "snapshots", "current");
    const document = await transaction.get(reference);
    if (!document.exists()) throw new Error("No encontré el estado financiero en Firebase.");
    const current = snapshotForDate({ ...(document.data() as FinancialSnapshot), source: "firestore" as const });
    const changed = await mutate(current, transaction, (collectionName, id) => doc(firestore, "users", uid, collectionName, id));
    const next = changed.skipSnapshotWrite
      ? changed.snapshot
      : assertAllocationsWithinNu({ ...changed.snapshot, source: "firestore" as const, updatedAt: nowIso() });
    if (!changed.skipSnapshotWrite) transaction.set(reference, next);
    return { result: changed.result ?? { ok: true }, snapshot: next };
  });
}

async function savingsBucketIds(uid: string): Promise<string[]> {
  const [{ collection, getDocs }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const documents = await getDocs(collection(firestore, "users", uid, "savingsBuckets"));
  return documents.docs.map((document) => document.id).filter((id) => id !== "DISPONIBLE");
}

async function cardSummary(uid: string, card: string, requested: "actual" | "anterior", cutDay: number): Promise<Record<string, unknown>> {
  const [{ collection, getDocs }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const documents = await getDocs(collection(firestore, "users", uid, "movements"));
  const snapshot = await readSnapshot(uid);
  return buildCardStatementSummary(documents.docs.map(item=>item.data() as Movement),card,requested,cutDay,dateOnly(),new Date(),snapshot.bankCredits ?? [],snapshot.cardPaymentTimingAdjustments ?? []);
}

async function updateYield(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.amount);
  if (amountCents < 0) throw new Error("El rendimiento no puede ser negativo.");
  const operationId = safeDocumentId(payload.operacionId, "rendimiento");
  const hasOpening = Object.prototype.hasOwnProperty.call(payload, "saldoInicialDisponible");
  const openingAvailableCents = hasOpening ? cents(payload.saldoInicialDisponible) : undefined;
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const fundReference = document("temporaryContributions", YIELD_FUND_ID);
    const [operationDocument, fundDocument] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(fundReference),
    ]);
    if (operationDocument.exists()) {
      const previous = operationDocument.data();
      if (previous.amountCents !== amountCents || previous.openingAvailableCents !== openingAvailableCents) {
        throw new Error("Este intento de actualización ya se usó con otros importes. Abre de nuevo el editor.");
      }
      return { snapshot, result: previous.result as Record<string, unknown>, skipSnapshotWrite: true };
    }
    const fundBefore = fundDocument.exists()
      ? { id: fundDocument.id, ...fundDocument.data() } as TemporaryContribution
      : undefined;
    if (fundBefore && !snapshot.yieldAllocationInitializedAt) {
      throw new Error("El fondo de rendimientos existe sin configuración inicial. No se guardó nada.");
    }
    if (snapshot.yieldAllocationInitializedAt && !fundBefore) {
      throw new Error("Falta el fondo de rendimientos. No se guardó nada; revisa la sincronización.");
    }
    if (!snapshot.yieldAllocationInitializedAt && !hasOpening) {
      throw new Error("Primero indica cuánto de los rendimientos sigue disponible en Nu.");
    }
    if (fundBefore?.active === false) throw new Error("El fondo de rendimientos está archivado. No se guardó nada.");
    const createdAt = nowIso();
    const next = applyYieldUpdate(snapshot, amountCents, createdAt,
      hasOpening || snapshot.yieldAllocationInitializedAt
        ? { availableBeforeCents: fundBefore?.balanceCents ?? 0, openingAvailableCents }
        : undefined);
    const fundDeltaCents = next.temporaryContributionCents - snapshot.temporaryContributionCents;
    let updatedFund: TemporaryContribution | undefined;
    let event: TemporaryContributionEvent | undefined;
    if (next.yieldAllocationInitializedAt) {
      updatedFund = fundBefore
        ? { ...fundBefore, balanceCents: fundBefore.balanceCents + fundDeltaCents, updatedAt: createdAt }
        : {
          id: YIELD_FUND_ID,
          description: YIELD_FUND_LABEL,
          balanceCents: fundDeltaCents,
          originalAmountCents: fundDeltaCents,
          active: true,
          order: Date.now(),
          createdAt,
          updatedAt: createdAt,
          origin: "app",
        };
      if (updatedFund.balanceCents < 0) throw new Error("El saldo disponible de rendimientos quedaría por debajo de cero.");
      if (!fundBefore || fundDeltaCents) {
        event = {
          id: `event_${operationId}`,
          contributionId: YIELD_FUND_ID,
          type: fundBefore ? "adjustment" : "create",
          amountDeltaCents: fundDeltaCents,
          beforeBalanceCents: fundBefore?.balanceCents ?? 0,
          afterBalanceCents: updatedFund.balanceCents,
          descriptionBefore: YIELD_FUND_LABEL,
          descriptionAfter: YIELD_FUND_LABEL,
          accountDeltaCents: amountCents - snapshot.yieldCents,
          note: fundBefore ? "Nueva captura del acumulado informado por Nu" : "Saldo inicial disponible de rendimientos",
          createdAt,
          origin: "app",
        };
        transaction.set(document("temporaryContributionEvents", event.id), event);
      }
      transaction.set(fundReference, updatedFund);
    }
    const result = {
      ok: true,
      acumulado: money(amountCents),
      ...(updatedFund ? { disponible: money(updatedFund.balanceCents) } : {}),
      fondosActualizados: updatedFund ? [updatedFund] : [],
      eventosDeFondos: event ? [event] : [],
    };
    transaction.set(operationReference, {
      id: operationId,
      kind: "update_yield",
      amountCents,
      ...(hasOpening ? { openingAvailableCents } : {}),
      createdAt,
      result,
    });
    return { snapshot: next, result };
  });
}

async function adjustWeek(uid: string, payload: Record<string, unknown>, transfer: boolean): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.monto);
  if (amountCents <= 0) throw new Error("El monto debe ser mayor que cero.");
  const operationId = safeDocumentId(payload.idOperacion, transfer ? "week_transfer" : "week_adjustment");
  const requestedOrigin = String(payload.origen ?? (transfer ? "" : "new"));
  const fundingSource: "new" | "temporary" = requestedOrigin === "temporary" ? "temporary" : "new";
  const selectedContributionIds = [...new Set((Array.isArray(payload.aportaciones) ? payload.aportaciones : []).map(String))]
    .filter((id) => id && !id.includes("/") && id.length <= 150);
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const contributionReferences = !transfer && fundingSource === "temporary"
      ? selectedContributionIds.filter((id) => id !== "legacy-temporary-total").map((id) => document("temporaryContributions", id))
      : [];
    const [operationDocument, ...contributionDocuments] = await Promise.all([
      transaction.get(operationReference),
      ...contributionReferences.map((reference) => transaction.get(reference)),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    const target = activeWeek(snapshot);
    const source = transfer ? snapshot.weeks.find((week) => week.id !== target.id && week.status === "closed") : undefined;
    const carryoverAvailable = Math.max(0, snapshot.previousPeriodCarryover?.availableCents ?? 0);
    const fromPreviousPeriod = transfer && (requestedOrigin === "period" || ((!source || source.availableCents <= 0) && carryoverAvailable > 0));
    if (transfer && fromPreviousPeriod && amountCents > carryoverAvailable) throw new Error("La quincena anterior no tiene saldo suficiente.");
    if (transfer && !fromPreviousPeriod && (!source || source.availableCents < amountCents)) throw new Error("La semana anterior no tiene saldo suficiente.");

    let temporaryAllocations: ReturnType<typeof allocateTemporaryContributions>["allocations"] = [];
    if (!transfer && fundingSource === "temporary") {
      if (!selectedContributionIds.length) throw new Error("Elige al menos una aportación temporal.");
      if (selectedContributionIds.includes("legacy-temporary-total")) {
        if (selectedContributionIds.length !== 1) throw new Error("Actualiza la lista de aportaciones antes de continuar.");
        if (amountCents > snapshot.temporaryContributionCents) throw new Error("El aporte temporal neto no alcanza para agregar ese dinero.");
        temporaryAllocations = [{
          id: "legacy-temporary-total",
          description: "Aporte temporal migrado",
          beforeCents: snapshot.temporaryContributionCents,
          usedCents: amountCents,
          afterCents: snapshot.temporaryContributionCents - amountCents,
        }];
      } else {
        const selected = contributionDocuments.map((item, index) => {
          if (!item.exists()) throw new Error("Una aportación seleccionada cambió o ya no existe.");
          return { id: contributionReferences[index].id, ...item.data() } as TemporaryContribution;
        });
        const selectedTotal = selected.reduce((sum, item) => sum + item.balanceCents, 0);
        const balancingEntry: TemporaryContribution = {
          id: "unselected-net-balance",
          description: "Otros fondos temporales",
          balanceCents: snapshot.temporaryContributionCents - selectedTotal,
          active: true,
          order: Number.MAX_SAFE_INTEGER,
        };
        const allocation = allocateTemporaryContributions([...selected, balancingEntry], selectedContributionIds, amountCents);
        temporaryAllocations = allocation.allocations;
        allocation.contributions.filter((item) => selectedContributionIds.includes(item.id)).forEach((item) => {
          transaction.set(document("temporaryContributions", item.id), { ...item, updatedAt: nowIso() }, { merge: true });
        });
      }
    }

    const changed = fromPreviousPeriod
      ? applyPreviousPeriodCarryover(snapshot, amountCents)
      : transfer ? {
          ...snapshot,
          weeks: snapshot.weeks.map((week) => week.id === target.id
            ? { ...week, availableCents: week.availableCents + amountCents }
            : source && week.id === source.id ? { ...week, availableCents: week.availableCents - amountCents } : week),
        } : applyWeekFunding(snapshot, amountCents, fundingSource);
    const createdAt = nowIso();
    temporaryAllocations.forEach((item, index) => {
      const event: TemporaryContributionEvent = {
        id: `event_${operationId}_${index}`,
        contributionId: item.id,
        type: "week_allocation",
        amountDeltaCents: -item.usedCents,
        beforeBalanceCents: item.beforeCents,
        afterBalanceCents: item.afterCents,
        descriptionBefore: item.description,
        descriptionAfter: item.description,
        accountDeltaCents: 0,
        note: `Asignado a ${target.label} de ${snapshot.periodLabel ?? "la quincena activa"}`,
        periodLabel: snapshot.periodLabel,
        createdAt,
        origin: "app",
      };
      transaction.set(document("temporaryContributionEvents", event.id), event);
    });
    const movement: Movement = {
      id: operationId,
      periodId: snapshot.periodLabel,
      kind: fromPreviousPeriod ? "previous_period_transfer" : transfer ? "previous_week_transfer" : "week_adjustment",
      amountCents,
      merchant: fromPreviousPeriod ? "Sobrante de la quincena anterior" : transfer ? "Transferencia entre semanas" : fundingSource === "temporary" ? "Aporte temporal a la semana" : "Dinero nuevo para la semana",
      description: fromPreviousPeriod
        ? `Transferencia desde ${snapshot.previousPeriodCarryover?.sourcePeriod ?? "la quincena anterior"}`
        : !transfer && fundingSource === "temporary" ? temporaryAllocations.map((item) => item.description).join(" + ") : undefined,
      paymentMethod: fromPreviousPeriod ? "Presupuesto anterior" : transfer ? "Presupuesto" : fundingSource === "temporary" ? "Aporte temporal" : "Depósito nuevo",
      weekId: target.id,
      accountDeltaCents: !transfer && fundingSource === "new" ? amountCents : 0,
      weekFundingSource: !transfer ? (fundingSource === "new" ? "new_deposit" : "temporary_contribution") : undefined,
      temporaryAllocationIds: temporaryAllocations.length ? temporaryAllocations.map((item) => item.id) : undefined,
      rolloverSourcePeriod: fromPreviousPeriod ? snapshot.previousPeriodCarryover?.sourcePeriod : undefined,
      rolloverDestinationPeriod: fromPreviousPeriod ? snapshot.periodLabel : undefined,
      rolloverId: fromPreviousPeriod ? snapshot.previousPeriodCarryover?.id : undefined,
      createdAt,
      spentOn: dateOnly(),
      syncState: "synced",
      origin: "app",
      schemaVersion: 3,
    };
    const next = { ...changed, movements: [movement, ...changed.movements].slice(0, 200) };
    transaction.set(document("movements", operationId), movement);
    const periodId = slug(String(snapshot.periodLabel ?? "period"));
    next.weeks.forEach((week) => transaction.set(document("weeks", `${periodId}_${week.id}`), { ...week, periodId, updatedAt: createdAt, origin: "app" }, { merge: true }));
    if (fromPreviousPeriod && next.previousPeriodCarryover) {
      transaction.set(document("periodRollovers", next.previousPeriodCarryover.id), { ...next.previousPeriodCarryover, updatedAt: createdAt, status: next.previousPeriodCarryover.availableCents > 0 ? "available" : "transferred" }, { merge: true });
    }
    transaction.set(document("weeklyAdjustments", operationId), {
      id: operationId,
      type: transfer ? "transfer" : "funding",
      periodId,
      weekId: target.id,
      amountCents,
      source: fromPreviousPeriod ? "previous_period" : transfer ? "previous_week" : fundingSource === "temporary" ? "temporary_contribution" : "new_deposit",
      accountDeltaCents: !transfer && fundingSource === "new" ? amountCents : 0,
      temporaryAllocations,
      createdAt,
      origin: "app",
    });
    const result = {
      ok: true,
      monto: money(amountCents),
      origen: fromPreviousPeriod ? "period" : transfer ? "week" : fundingSource,
      cambioNu: money(!transfer && fundingSource === "new" ? amountCents : 0),
      aporteTemporalRestante: money(next.temporaryContributionCents),
      restanteAnterior: money(next.previousPeriodCarryover?.availableCents ?? 0),
      asignaciones: temporaryAllocations.map((item) => ({ id: item.id, concepto: item.description, usado: money(item.usedCents), saldoDespues: money(item.afterCents) })),
    };
    transaction.set(operationReference, { id: operationId, kind: transfer ? "week_transfer" : "week_adjustment", createdAt, result });
    return { snapshot: next, result };
  });
}

type PendingTransaction = Pick<import("firebase/firestore").Transaction, "get" | "set">;
type PendingDocument = (collection: string, id: string) => import("firebase/firestore").DocumentReference;

async function applyPendingPayment(payload: Record<string, unknown>, reimbursement: boolean, snapshot: FinancialSnapshot, transaction: PendingTransaction, documentRef: PendingDocument): Promise<{ snapshot: FinancialSnapshot; result: Record<string, unknown>; skipSnapshotWrite?: boolean }> {
  const movementId = String(payload.movementId ?? "");
  if (!movementId) throw new Error("No se encontró el movimiento pendiente.");
  const operationId = safeDocumentId(payload.operacionId, reimbursement ? "pending_reimbursement" : "pending_payment");
  const legacyOwnSource = payload.descontarSemana !== false ? "week" : "new";
  const requestedSource = String(payload.fuente ?? (reimbursement ? "new" : legacyOwnSource)).toLowerCase();
  if (!["new", "week", "savings", "fund"].includes(requestedSource)) {
    throw new Error(reimbursement ? "Elige de dónde debe salir el reembolso." : "Elige de dónde debe salir el abono.");
  }
  const request = { movementId, amountCents: reimbursement ? 0 : cents(payload.monto), source: requestedSource, sourceId: String(payload.fuenteId ?? "") };
    const reference = documentRef("movements", movementId);
    const operationReference = documentRef("operations", operationId);
    const [movementDocument, operationDocument] = await Promise.all([transaction.get(reference), transaction.get(operationReference)]);
    if (operationDocument.exists()) {
      const previous = operationDocument.data();
      if (previous.request && Object.entries(request).some(([key, value]) => previous.request[key] !== value)) throw new Error("Este identificador ya corresponde a otro abono o reembolso.");
      return { snapshot, result: previous.result as Record<string, unknown>, skipSnapshotWrite: true };
    }
    const movement = (movementDocument.exists() ? movementDocument.data() : undefined) as Movement | undefined;
    if (!movement || movement.kind !== "expense") throw new Error("El movimiento ya no existe.");
    if (!reimbursement && !isTrackedCard(movement.paymentMethod)) throw new Error("Este movimiento no tiene una parte propia pendiente de tarjeta.");
    if (!reimbursement) {
      const payment = movement.cardPeriod ? await transaction.get(documentRef("cardPaymentEvents", cardPaymentEventId(movement.paymentMethod, movement.cardPeriod))) : undefined;
      if (payment?.exists() || movement.cardPaymentStatus === "paid" || movement.cardPaymentEventId || movement.historicalSettlement) throw new Error("Este corte ya fue pagado. No se pueden agregar abonos a sus compras.");
    }
    if (reimbursement && payload.expectedAmountCents != null) {
      const payment = isTrackedCard(movement.paymentMethod) && movement.cardPeriod
        ? await transaction.get(documentRef("cardPaymentEvents", cardPaymentEventId(movement.paymentMethod, movement.cardPeriod))) : undefined;
      if (payment?.exists() || movement.cardPaymentStatus === "paid" || movement.cardPaymentEventId || movement.historicalSettlement) throw new Error("Un corte seleccionado ya fue pagado. No se marcó ningún reembolso.");
      if (pendingBreakdown(movement).reimbursementPendingCents !== payload.expectedAmountCents) throw new Error("Un reembolso cambió o ya fue recibido. Revisa la selección; no se marcó ninguno.");
    }
    const recognized = recognizeDueInstallment(snapshot, movement);
    let next = { ...recognized.movement };
    let updated = recognized.snapshot;
    let appliedAmountCents = 0;
    let sourceResult: Record<string, unknown> = {};
    const pending = pendingBreakdown(next);
    const amount = reimbursement ? pending.reimbursementPendingCents : cents(payload.monto);
    if (reimbursement && !amount) throw new Error("Este reembolso ya fue registrado.");
    if (!reimbursement && (amount <= 0 || amount > pending.ownPendingCents)) throw new Error("El abono supera tu parte pendiente.");
    appliedAmountCents = amount;
    const createdAt = nowIso();
    let source: PendingFundingSource;
    let sourceLabel = "Dinero nuevo";
    let contributionBefore: TemporaryContribution | undefined;
    if (requestedSource === "week") {
      const week = activeWeek(updated);
      const requestedWeekId = String(payload.fuenteId ?? week.id);
      if (requestedWeekId !== week.id) throw new Error("La semana elegida ya no está activa. Actualiza y vuelve a intentarlo.");
      source = { kind: "week", sourceId: week.id, periodId: updated.periodLabel };
      sourceLabel = week.label;
    } else if (requestedSource === "savings") {
      source = { kind: "savings", sourceId: "DISPONIBLE" };
      sourceLabel = "Ahorro disponible";
    } else if (requestedSource === "fund") {
      const rawContributionId = String(payload.fuenteId ?? "").trim();
      if (!rawContributionId) throw new Error(reimbursement ? "Elige el fondo donde está contabilizado el reembolso." : "Elige el fondo que financiará el abono.");
      const contributionId = safeDocumentId(rawContributionId, "fondo");
      if (reimbursement && contributionId === YIELD_FUND_ID) {
        throw new Error("Los rendimientos reservados no representan un reembolso recibido.");
      }
      const contributionDocument = await transaction.get(documentRef("temporaryContributions", contributionId));
      const legacyTotal = contributionId === "legacy-temporary-total" && !contributionDocument.exists();
      if (!contributionDocument.exists() && !legacyTotal) throw new Error("El fondo elegido ya no existe.");
      contributionBefore = legacyTotal
        ? { id: contributionId, description: "Aporte temporal migrado", balanceCents: updated.temporaryContributionCents, originalAmountCents: updated.temporaryContributionCents, active: true, order: 0, origin: "migration" }
        : { id: contributionDocument.id, ...contributionDocument.data() } as TemporaryContribution;
      source = { kind: "fund", sourceId: contributionId };
      sourceLabel = contributionBefore.description;
    } else {
      source = { kind: "new" };
    }

    const funding = applyPendingFundingChange(updated, amount, source, contributionBefore);
    updated = funding.snapshot;
    if (!reimbursement && source.kind === "week" && movement.installmentPurchaseId) {
      updated = { ...updated, weeks: updated.weeks.map((item) => item.id === source.sourceId ? {
        ...item,
        categorySpend: categoryWithDelta({ ...updated, categorySpend: item.categorySpend ?? [] }, movement.category, amount),
      } : item) };
    }
    const sourceMovementId = `${reimbursement ? "reimbursement" : "pending_payment"}_source_${operationId}`;
    if (source.kind === "week") {
      const sourceWeek = updated.weeks.find((week) => week.id === source.sourceId);
      if (!sourceWeek) throw new Error("La semana elegida ya no está disponible.");
      const periodId = slug(String(updated.periodLabel ?? "period"));
      transaction.set(documentRef("weeks", `${periodId}_${sourceWeek.id}`), { ...sourceWeek, periodId, updatedAt: createdAt, origin: "app" }, { merge: true });
      transaction.set(documentRef("weeklyAdjustments", sourceMovementId), {
        id: sourceMovementId,
        type: reimbursement ? "reimbursement_allocation" : "pending_payment_allocation",
        periodId,
        weekId: sourceWeek.id,
        amountCents: amount,
        movementId,
        card: movement.paymentMethod,
        createdAt,
        origin: "app",
      });
    }
    if (source.kind === "savings") {
      const sourceMovement: Movement = {
        id: sourceMovementId,
        periodId: updated.periodLabel,
        kind: "savings_withdrawal",
        amountCents: amount,
        merchant: reimbursement ? "Reembolso recibido" : "Abono a tarjeta",
        description: `${reimbursement ? "Reembolso" : "Abono"} para ${movement.description ?? movement.merchant}`,
        paymentMethod: movement.paymentMethod,
        relatedMovementId: movementId,
        savingsBucketId: "DISPONIBLE",
        savingsSource: `Reserva de ${movement.paymentMethod}`,
        spentOn: dateOnly(),
        createdAt,
        syncState: "synced",
        origin: "app",
        schemaVersion: 3,
      };
      transaction.set(documentRef("movements", sourceMovementId), sourceMovement);
      updated = { ...updated, movements: [sourceMovement, ...updated.movements].slice(0, 200) };
    }
    if (source.kind === "fund" && contributionBefore && funding.contribution) {
      const updatedContribution: TemporaryContribution = { ...funding.contribution, updatedAt: createdAt };
      const event: TemporaryContributionEvent = {
        id: `event_${sourceMovementId}`,
        contributionId: updatedContribution.id,
        type: reimbursement ? "reimbursement_allocation" : "pending_payment_allocation",
        amountDeltaCents: -amount,
        beforeBalanceCents: contributionBefore.balanceCents,
        afterBalanceCents: updatedContribution.balanceCents,
        descriptionBefore: contributionBefore.description,
        descriptionAfter: updatedContribution.description,
        accountDeltaCents: 0,
        note: `${reimbursement ? "Reembolso" : "Abono"} para ${movement.description ?? movement.merchant}`,
        createdAt,
        origin: "app",
      };
      transaction.set(documentRef("temporaryContributions", updatedContribution.id), updatedContribution, { merge: true });
      transaction.set(documentRef("temporaryContributionEvents", event.id), event);
      sourceResult = { fondosActualizados: [updatedContribution], eventosDeFondos: [event] };
    }

    if (reimbursement) {
      const coveredAmountCents = Math.min(pending.billedCents, pending.coveredCents + amount);
      const pendingAmountCents = Math.max(0, pending.movementPendingCents - amount);
      next = {
        ...next,
        reimbursementReceived: true,
        reimbursementFundingSource: source.kind,
        reimbursementFundingSourceId: source.sourceId,
        reimbursementFundingSourcePeriodId: source.periodId,
        reimbursementFundingSourceLabel: sourceLabel,
        coveredAmountCents,
        pendingAmountCents,
        cardStatus: pendingAmountCents === 0 ? "Cubierto" : "Parcial",
        accountDeltaCents: inferredAccountDelta(movement) + funding.accountDeltaCents,
        syncState: "synced",
      };
      updated = {
        ...updated,
        cardPayments: isTrackedCard(movement.paymentMethod)
          ? updateCardReserve(updated, movement.paymentMethod, amount)
          : updated.cardPayments,
      };
    } else {
      const movementPending = pending.movementPendingCents;
      const allocation: PendingPaymentAllocation = { operationId, amountCents: amount, source: source.kind, sourceId: source.sourceId, sourcePeriodId: source.periodId, sourceLabel, createdAt };
      next = {
        ...next,
        weekId: source.kind === "week" ? source.sourceId as Movement["weekId"] : next.weekId,
        coveredAmountCents: pending.coveredCents + amount,
        pendingAmountCents: movementPending - amount,
        laterContributionCents: (movement.laterContributionCents ?? 0) + amount,
        pendingPaymentAllocations: [...(movement.pendingPaymentAllocations ?? []), allocation],
        correctionOwnAllocations: movement.correctionOwnAllocations ? [...movement.correctionOwnAllocations, { source: source.kind, sourceId: source.sourceId, periodId: source.periodId, amountCents: amount, role: "payment" }] : undefined,
        accountDeltaCents: inferredAccountDelta(movement) + funding.accountDeltaCents,
        cardStatus: movementPending === amount ? "Cubierto" : "Parcial",
        syncState: "synced",
      };
      updated = {
        ...updated,
        cardPayments: updateCardReserve(updated, movement.paymentMethod, amount),
      };
    }
    sourceResult = {
      ...sourceResult,
      fuente: source.kind,
      fuenteId: source.sourceId ?? "",
      fuenteDescripcion: sourceLabel,
      cambioNu: money(funding.accountDeltaCents),
    };
    next.updatedAt = createdAt;
    transaction.set(reference, next);
    const result = { ok: true, movimientoId: movementId, monto: money(appliedAmountCents), tipo: reimbursement ? "reimbursement" : "own_payment", ...sourceResult };
    transaction.set(operationReference, { id: operationId, kind: reimbursement ? "pending_reimbursement" : "pending_payment", createdAt: next.updatedAt, request, result });
    return { snapshot: replaceMovement(updated, next), result };
}

async function pendingPayment(uid: string, payload: Record<string, unknown>, reimbursement: boolean): Promise<Record<string, unknown>> {
  return mutateSnapshot(uid, (snapshot, transaction, documentRef) => applyPendingPayment(payload, reimbursement, snapshot, transaction, documentRef));
}

async function pendingReimbursementBatch(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { items, source, sourceId, operationId } = parseReimbursementBatch(payload);
  const request = JSON.stringify({ items, source, sourceId });
  return mutateSnapshot(uid, async (snapshot, transaction, documentRef) => {
    const operationReference = documentRef("operations", operationId);
    const previous = await transaction.get(operationReference);
    if (previous.exists()) {
      if (previous.data().request !== request) throw new Error("Este intento ya corresponde a otra selección de reembolsos.");
      return { snapshot, result: previous.data().result as Record<string, unknown>, skipSnapshotWrite: true };
    }
    // Firestore requires all reads before writes. Keep a local overlay so a shared
    // fund is consumed cumulatively, while committing every item atomically.
    type Ref = import("firebase/firestore").DocumentReference;
    type Data = import("firebase/firestore").DocumentData;
    const staged = new Map<string, { reference: Ref; data: Data; merge: boolean }>();
    const buffered = {
      get: async (reference: Ref) => {
        const original = await transaction.get(reference);
        const pending = staged.get(reference.path);
        if (!pending) return original;
        const data = pending.merge ? { ...original.data(), ...pending.data } : pending.data;
        return { ...original, id: reference.id, exists: () => true, data: () => data };
      },
      set: (reference: Ref, data: Data, options?: { merge?: boolean }) => {
        const previousWrite = staged.get(reference.path);
        const merge = options?.merge === true;
        staged.set(reference.path, { reference, data: merge && previousWrite ? { ...previousWrite.data, ...data } : data, merge: previousWrite ? previousWrite.merge && merge : merge });
        return buffered;
      },
    } as PendingTransaction;
    let next = snapshot;
    const movements: Movement[] = [];
    const funds = new Map<string, TemporaryContribution>();
    const events: TemporaryContributionEvent[] = [];
    for (const [index, item] of items.entries()) {
      const changed = await applyPendingPayment({ movementId: item.movementId, expectedAmountCents: item.amountCents, fuente: source, fuenteId: sourceId, operacionId: `${operationId}_item_${index}` }, true, next, buffered, documentRef);
      if (changed.skipSnapshotWrite) throw new Error("El intento está incompleto. Revisa la selección antes de continuar.");
      next = changed.snapshot;
      movements.push(next.movements.find(movement => movement.id === item.movementId)!);
      for (const fund of (changed.result.fondosActualizados ?? []) as TemporaryContribution[]) funds.set(fund.id, fund);
      events.push(...(changed.result.eventosDeFondos ?? []) as TemporaryContributionEvent[]);
    }
    assertAllocationsWithinNu(next);
    const result = { ok: true, cantidad: items.length, totalCents: items.reduce((sum, item) => sum + item.amountCents, 0), movimientosActualizados: movements, fondosActualizados: [...funds.values()], eventosDeFondos: events };
    for (const write of staged.values()) {
      if (write.merge) transaction.set(write.reference, write.data, { merge: true });
      else transaction.set(write.reference, write.data);
    }
    transaction.set(operationReference, { id: operationId, kind: "pending_reimbursements", request, createdAt: nowIso(), result });
    return { snapshot: next, result };
  });
}

async function savingsMovement(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.monto);
  const contribution = String(payload.tipo) === "Aporte";
  const bucketId = String(payload.apartadoId ?? "DISPONIBLE");
  if (amountCents <= 0) throw new Error("El monto debe ser mayor que cero.");
  const operationId = safeDocumentId(payload.idOperacion, "saving");
  const bucketIds = await savingsBucketIds(uid);
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const fromWeek = contribution && String(payload.fuente) === "Semana actual";
    const toWeek = !contribution && String(payload.destino) === "Semana actual";
    const week = fromWeek || toWeek ? activeWeek(snapshot) : undefined;
    if (fromWeek && week && amountCents > week.availableCents) throw new Error("La semana activa no tiene saldo suficiente.");
    const bucketRefs = bucketIds.map((id) => document("savingsBuckets", id));
    const operationReference = document("operations", operationId);
    const movementReference = document("movements", operationId);
    const [operationDocument, existingMovement, ...bucketDocuments] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(movementReference),
      ...bucketRefs.map((reference) => transaction.get(reference)),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (existingMovement.exists()) {
      const result = { ok: true, id: operationId, yaRegistrado: true };
      transaction.set(operationReference, { id: operationId, kind: "savings_movement", createdAt: nowIso(), result });
      return { snapshot, result };
    }
    const buckets = new Map(bucketDocuments.map((bucketDocument) => [bucketDocument.id, bucketDocument.data()]));
    const assignedTotal = bucketDocuments.reduce((sum, bucketDocument) => sum + Math.max(0, Number(bucketDocument.data()?.balanceCents ?? 0)), 0);
    const defaultAvailable = Math.max(0, snapshot.savingsCents - assignedTotal);
    const selectedBucket = bucketId === "DISPONIBLE" ? undefined : buckets.get(bucketId);
    if (bucketId !== "DISPONIBLE" && !selectedBucket) throw new Error("El apartado ya no existe.");
    const sourceAvailable = bucketId === "DISPONIBLE" ? defaultAvailable : Math.max(0, Number(selectedBucket?.balanceCents ?? 0));
    if (!contribution && amountCents > sourceAvailable) throw new Error("Este apartado no tiene saldo suficiente.");
    if (!contribution && selectedBucket?.protected === true && !payload.confirmadoProtegido) throw new Error("Confirma que deseas usar dinero protegido.");
    if (!contribution && externalSavingsWithdrawal(payload) && amountCents > (snapshot.accounts[0]?.balanceCents ?? 0)) throw new Error("El saldo de Nu no alcanza para este retiro.");
    const movement: Movement = { id: operationId, periodId: snapshot.periodLabel, kind: contribution ? "savings_contribution" : "savings_withdrawal", amountCents, merchant: String(payload.objetivo || (contribution ? "Aporte de ahorro" : "Retiro de ahorro")), description: String(payload.nota || payload.objetivo || (contribution ? "Aporte de ahorro" : "Retiro de ahorro")), paymentMethod: String(payload.fuente || payload.destino || "Ahorro"), weekId: week?.id, spentOn: dateOnly(), createdAt: nowIso(), syncState: "synced", savingsBucketId: bucketId, savingsSource: String(payload.fuente || payload.destino || ""), origin: "app", schemaVersion: 3 };
    const externalDelta = contribution && !fromWeek ? amountCents : !contribution && !toWeek ? -amountCents : 0;
    const weekAdjusted = fromWeek
      ? applySavingsWeekTransfer(snapshot, amountCents, "to_savings")
      : toWeek ? applySavingsWeekTransfer(snapshot, amountCents, "to_week") : snapshot;
    if (bucketId !== "DISPONIBLE") {
      const bucketRef = document("savingsBuckets", bucketId);
      const bucket = selectedBucket ?? {};
      const balance = Math.max(0, Number(bucket.balanceCents ?? 0) + (contribution ? amountCents : -amountCents));
      transaction.set(bucketRef, { ...bucket, balanceCents: balance, updatedAt: nowIso() });
    }
    transaction.set(movementReference, movement);
    const protectedDelta = selectedBucket?.protected === true ? (contribution ? amountCents : -amountCents) : 0;
    const result = { ok: true, id: operationId, tipo: contribution ? "Aporte" : "Retiro", monto: money(amountCents) };
    transaction.set(operationReference, { id: operationId, kind: "savings_movement", createdAt: movement.createdAt, result });
    const allocatedDelta = bucketId === "DISPONIBLE" ? 0 : contribution ? amountCents : -amountCents;
    return { snapshot: { ...weekAdjusted, accounts: accountWithDelta(snapshot, externalDelta), savingsCents: snapshot.savingsCents + (contribution ? amountCents : -amountCents), protectedSavingsCents: Math.max(0, snapshot.protectedSavingsCents + protectedDelta), allocatedSavingsCents: Math.max(0, (snapshot.allocatedSavingsCents ?? assignedTotal) + allocatedDelta), savingsThisPeriodCents: contribution ? snapshot.savingsThisPeriodCents + amountCents : snapshot.savingsThisPeriodCents, movements: [movement, ...snapshot.movements].slice(0, 200) }, result };
  });
}

function externalSavingsWithdrawal(payload: Record<string, unknown>): boolean {
  return String(payload.tipo) !== "Aporte" && String(payload.destino) !== "Semana actual";
}

async function savingsBucket(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const baseId = String(payload.id || payload.nombre || "apartado").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 50) || `APARTADO_${crypto.randomUUID()}`;
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const reference = document("savingsBuckets", baseId);
    const existing = await transaction.get(reference);
    const current = existing.data() ?? {};
    const balanceCents = Math.max(0, Number(current.balanceCents ?? 0));
    const wasProtected = current.protected === true;
    const protectedBucket = Boolean(payload.protegido);
    transaction.set(reference, { id: baseId, name: String(payload.nombre), color: String(payload.color || "#1f7a4d"), goalCents: cents(payload.meta), protected: protectedBucket, active: true, order: Number(current.order ?? Date.now()), balanceCents, updatedAt: nowIso(), origin: existing.exists() ? current.origin ?? "app" : "app" });
    const protectedDelta = wasProtected === protectedBucket ? 0 : protectedBucket ? balanceCents : -balanceCents;
    return { snapshot: { ...snapshot, protectedSavingsCents: Math.max(0, snapshot.protectedSavingsCents + protectedDelta) }, result: { ok: true, id: baseId } };
  });
}

async function savingsTransfer(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.monto);
  const fromId = String(payload.origenId); const toId = String(payload.destinoId);
  if (amountCents <= 0 || fromId === toId) throw new Error("Revisa los apartados y el monto.");
  const operationId = safeDocumentId(payload.idOperacion, "savings_transfer");
  const bucketIds = await savingsBucketIds(uid);
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const refs = bucketIds.map((id) => document("savingsBuckets", id));
    const operationReference = document("operations", operationId);
    const movementReference = document("movements", operationId);
    const [operationDocument, existingMovement, ...docs] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(movementReference),
      ...refs.map((ref) => transaction.get(ref)),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (existingMovement.exists()) {
      const result = { ok: true, id: operationId, yaRegistrado: true };
      transaction.set(operationReference, { id: operationId, kind: "savings_transfer", createdAt: nowIso(), result });
      return { snapshot, result };
    }
    const data = new Map(docs.map((document) => [document.id, document.data()]));
    const otherTotal = docs.reduce((sum, document) => sum + Math.max(0, Number(document.data()?.balanceCents ?? 0)), 0);
    const availableDefault = Math.max(0, snapshot.savingsCents - otherTotal);
    const sourceBalance = fromId === "DISPONIBLE" ? availableDefault : Number(data.get(fromId)?.balanceCents ?? 0);
    if (fromId !== "DISPONIBLE" && !data.has(fromId)) throw new Error("El apartado de origen ya no existe.");
    if (toId !== "DISPONIBLE" && !data.has(toId)) throw new Error("El apartado de destino ya no existe.");
    if (amountCents > sourceBalance) throw new Error("El apartado de origen no tiene saldo suficiente.");
    const sourceProtected = fromId !== "DISPONIBLE" && data.get(fromId)?.protected === true;
    const destinationProtected = toId !== "DISPONIBLE" && data.get(toId)?.protected === true;
    if (sourceProtected && !payload.confirmadoProtegido) throw new Error("Confirma que deseas mover dinero protegido.");
    refs.forEach((ref) => {
      const bucket = data.get(ref.id) ?? {};
      const delta = ref.id === fromId ? -amountCents : ref.id === toId ? amountCents : 0;
      if (delta) transaction.set(ref, { ...bucket, balanceCents: Math.max(0, Number(bucket.balanceCents ?? 0) + delta), updatedAt: nowIso() });
    });
    const protectedDelta = sourceProtected === destinationProtected ? 0 : destinationProtected ? amountCents : -amountCents;
    const allocatedDelta = fromId === "DISPONIBLE" ? amountCents : toId === "DISPONIBLE" ? -amountCents : 0;
    const createdAt = nowIso();
    const movement: Movement = {
      id: operationId,
      periodId: snapshot.periodLabel,
      kind: "savings_transfer",
      amountCents,
      merchant: "Movimiento entre apartados",
      description: `${fromId} → ${toId}`,
      paymentMethod: "Ahorro",
      spentOn: dateOnly(),
      createdAt,
      syncState: "synced",
      savingsBucketId: toId,
      savingsSource: fromId,
      origin: "app",
      schemaVersion: 3,
    };
    const result = { ok: true, id: operationId, tipo: "Transferencia", monto: money(amountCents), origenId: fromId, destinoId: toId };
    transaction.set(movementReference, movement);
    transaction.set(operationReference, { id: operationId, kind: "savings_transfer", createdAt, result });
    return { snapshot: { ...snapshot, protectedSavingsCents: Math.max(0, snapshot.protectedSavingsCents + protectedDelta), allocatedSavingsCents: Math.max(0, (snapshot.allocatedSavingsCents ?? otherTotal) + allocatedDelta), movements: [movement, ...snapshot.movements].slice(0, 200) }, result };
  });
}

async function reconciliation(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const snapshot = await readSnapshot(uid);
  const real = cents(payload.saldoReal);
  if (!Number.isSafeInteger(real) || real < 0) throw new Error("El saldo real de Nu no es válido.");
  const app = snapshot.accounts[0]?.balanceCents ?? 0;
  const difference = real - app;
  const id = `reconciliation_${crypto.randomUUID()}`;
  const checkedAt = nowIso();
  const [{ doc, setDoc }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  await setDoc(doc(firestore, "users", uid, "reconciliations", id), { id, checkedAt, checkedDate: dateOnly(), account: "NU", appBalanceCents: app, realBalanceCents: real, differenceCents: difference, status: difference === 0 ? "CONCILIADO" : "PENDIENTE", origin: "app" });
  const candidates = difference === 0 ? [] : [
    { confianza: "Revisar", titulo: difference > 0 ? "Puede faltar un ingreso o reembolso" : "Puede faltar un gasto o retiro", detalle: "Busca primero movimientos recientes o capturas todavía pendientes.", etiquetaAccion: "Ver movimientos", accion: "MOVIMIENTOS" },
    ...(difference > 0 ? [{ confianza: "Posible", titulo: "Reembolso recibido no confirmado", detalle: "Comprueba si ya te regresaron dinero de una compra compartida.", etiquetaAccion: "Ver pendientes", accion: "PENDIENTES" }] : []),
  ];
  return { ultima: { fecha: checkedAt, saldoApp: money(app), saldoReal: money(real), diferencia: money(difference), estado: difference === 0 ? "CONCILIADO" : "PENDIENTE" }, candidatos: candidates };
}

async function cardSettings(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const [{ doc, getDoc, setDoc }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const reference = doc(firestore, "users", uid, "views", "settings");
  const document = await getDoc(reference);
  const current = document.data()?.data ?? {};
  const cards = Array.isArray(current.tarjetas) ? current.tarjetas.slice() : [];
  const id = String(payload.id || payload.nombre || `card_${crypto.randomUUID()}`).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const card = { ...payload, id, integrada: true };
  const index = cards.findIndex((item: Record<string, unknown>) => String(item.id) === id);
  if (index >= 0) cards[index] = { ...cards[index], ...card }; else cards.push(card);
  await setDoc(reference, { data: { ...current, tarjetas: cards }, updatedAt: nowIso(), ok: true, lastAction: "card_settings" });
  return { ok: true, id };
}

async function cardPaymentStates(uid: string): Promise<Record<string, unknown>> {
  const snapshot = await readSnapshot(uid);
  const [{ collection, doc, getDoc, getDocs }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const [documents, paymentDocuments, settingsDocument] = await Promise.all([
    getDocs(collection(firestore, "users", uid, "movements")),
    getDocs(collection(firestore, "users", uid, "cardPaymentEvents")),
    getDoc(doc(firestore, "users", uid, "views", "settings")),
  ]);
  const allMovements = documents.docs.map((document) => document.data() as Movement);
  const paymentEvents = paymentDocuments.docs.map((document) => document.data() as Record<string, unknown>);
  const configuredCards = Array.isArray(settingsDocument.data()?.data?.tarjetas)
    ? settingsDocument.data()?.data?.tarjetas as Array<Record<string, unknown>>
    : [];
  const configuredCutDay = (card: string, fallback: number): number => {
    const configured = configuredCards.find((item) => String(item.medio ?? item.nombre ?? "") === card);
    const value = Number(configured?.diaCorte ?? fallback);
    return Number.isInteger(value) && value >= 1 && value <= 31 ? value : fallback;
  };
  const state = (card: string, cutDay: number) => {
    const reserve = snapshot.cardPayments.find((item) => item.name === card);
    const cuts = includeInheritedCardCut(
      cardCutStates(allMovements, paymentEvents, card, dateOnly(), snapshot.bankCredits ?? [], snapshot.cardPaymentTimingAdjustments ?? []),
      reserve?.unassignedPeriod,
      reserve?.unassignedCents,
      dateOnly(),
    );
    const period = cuts.find((cut) => cut.start <= dateOnly() && cut.end >= dateOnly())?.period
      ?? cardPeriodForDate(dateOnly(), cutDay)
      ?? "Periodo actual";
    return {
      periodoActual: period,
      saldo: money(reserve?.amountCents ?? 0),
      cortes: cuts.map((cut) => ({
        periodo: cut.period,
        inicio: cut.start,
        fin: cut.end,
        movimientos: cut.movementCount,
        parcialidadesPorConfirmar: cut.expectedCount ?? 0,
        previsto: money(cut.expectedCents ?? 0),
        total: money(cut.totalCents),
        bonificaciones: money(cut.bankCreditCents ?? 0),
        ajusteEntreCortes: money(cut.paymentTimingCents ?? 0),
        neto: money(cut.netCents ?? cut.totalCents),
        liberarAlPagar: money(cut.creditRemainderCents ?? 0),
        reembolsos: money(cut.reimbursementCents),
        reembolsosPendientes: money(cut.pendingReimbursementCents),
        cubierto: money(cut.coveredCents),
        pendientePropio: money(cut.ownPendingCents),
        reservaHeredada: money(reserve?.unassignedPeriod === cut.period ? Math.max(0, reserve.unassignedCents ?? 0) : 0),
        pagoCompleto: money(Math.max(0,cut.coveredCents + cut.pendingReimbursementCents + (reserve?.unassignedPeriod === cut.period ? Math.max(0, reserve.unassignedCents ?? 0) : 0) - (cut.creditRemainderCents ?? 0) + (cut.paymentTimingCents ?? 0))),
        cerrado: cut.closed,
        pagado: cut.paid,
      })),
    };
  };
  return {
    bbva: state("BBVA", configuredCutDay("BBVA", 12)),
    amex: state("AMEX Servicios", configuredCutDay("AMEX Servicios", 16)),
    platinum: state("American Express Platinum Card", configuredCutDay("American Express Platinum Card", 27)),
    scotia: state("Scotiabank", configuredCutDay("Scotiabank", 16)),
  };
}

async function cardPayment(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.monto);
  const card = String(payload.tarjeta);
  const period = String(payload.periodo ?? "");
  const paidOn = String(payload.fecha || dateOnly());
  const bounds = period.match(/^(\d{4}-\d{2}-\d{2})\s+al\s+(\d{4}-\d{2}-\d{2})$/);
  if (amountCents <= 0) throw new Error("El pago debe ser mayor que cero.");
  if (!isTrackedCard(card)) throw new Error("Esta tarjeta no está integrada al flujo de pagos.");
  if (!bounds) throw new Error("Elige el corte que estás pagando.");
  if (bounds[2] >= paidOn) throw new Error("Ese corte todavía no había terminado en la fecha del pago.");
  const operationId = safeDocumentId(payload.operacionId, "payment");
  const paymentEventId = cardPaymentEventId(card, period);
  const [{ collection, getDocs }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const movementDocuments = (await getDocs(collection(firestore, "users", uid, "movements"))).docs
    .filter((document) => {
      const movement = document.data() as Movement;
      return movement.kind === "expense" && movement.paymentMethod === card && movement.cardPeriod === period;
    });
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const paymentEventReference = document("cardPaymentEvents", paymentEventId);
    const timingPrerequisites = (snapshot.cardPaymentTimingAdjustments ?? [])
      .filter((item) => item.card === card && item.sourcePeriod === period)
      .map((item) => document("cardPaymentEvents", cardPaymentEventId(card, item.paymentPeriod)));
    const movementIds = cardPaymentMovementIds(
      movementDocuments.map((movementDocument) => ({ id: movementDocument.id, ...movementDocument.data() } as Movement)),
      snapshot.movements,
      card,
      period,
    );
    const movementReferences = movementIds.map((id) => document("movements", id));
    const [operationDocument, paymentEventDocument, ...otherDocuments] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(paymentEventReference),
      ...timingPrerequisites.map((reference) => transaction.get(reference)),
      ...movementReferences.map((reference) => transaction.get(reference)),
    ]);
    const prerequisiteDocuments = otherDocuments.slice(0, timingPrerequisites.length);
    const movementSnapshots = otherDocuments.slice(timingPrerequisites.length);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (paymentEventDocument.exists()) throw new Error("Este corte ya fue pagado; no se descontó nuevamente.");
    if (prerequisiteDocuments.some((document) => !document.exists())) throw new Error("Primero registra el pago del corte anterior al que se aplicó la bonificación.");
    const movements = movementSnapshots.map((movementDocument) => {
      if (!movementDocument.exists()) throw new Error("Un movimiento del corte cambió. Vuelve a abrir el pago.");
      return { id: movementDocument.id, ...movementDocument.data() } as Movement;
    });
    if (movements.some((movement) => movement.cardPeriod !== period || movement.paymentMethod !== card)) throw new Error("Los movimientos del corte cambiaron. Vuelve a intentarlo.");
    assertCutConfirmedForPayment(movements);
    const cardReserve = snapshot.cardPayments.find((item) => item.name === card);
    const inheritedForPeriod = cardReserve?.unassignedPeriod === period ? Math.max(0, cardReserve.unassignedCents ?? 0) : 0;
    if (!movements.length && !inheritedForPeriod) throw new Error("No encontré movimientos ni reserva heredada para ese corte.");
    const bankCredit = creditForCut(snapshot.bankCredits ?? [],card,period);
    const paymentTimingCents = paymentTimingForCut(snapshot.cardPaymentTimingAdjustments ?? [], card, period);
    const requirement = cardPaymentRequirement(movements, inheritedForPeriod,bankCredit.remainderCents,paymentTimingCents);
    if (requirement.ownPendingCents > 0) throw new Error(`Todavía falta apartar ${money(requirement.ownPendingCents).toLocaleString("es-MX", { style: "currency", currency: "MXN" })} de tu dinero en los movimientos de este corte.`);
    if (amountCents !== requirement.exactPaymentCents) throw new Error(`Para cerrar todo el corte, el pago debe ser exactamente ${money(requirement.exactPaymentCents).toLocaleString("es-MX", { style: "currency", currency: "MXN" })}.`);
    const settlement = movements.length
      ? settleCardPeriodMovements(movements, card, period, paymentEventId, paidOn)
      : { movements: [], settled: [], reimbursementsSettledCents: 0 };
    const reserved = snapshot.cardPayments.find((item) => item.name === card)?.amountCents ?? 0;
    const creditedSnapshot = creditCardReimbursements(snapshot, card, settlement.reimbursementsSettledCents);
    const reservedAfterReimbursements = reserved + settlement.reimbursementsSettledCents;
    const paidSnapshot = applyCardPaymentToSnapshot(creditedSnapshot, card, amountCents);
    // Release only cash belonging to this paid cut, never another cut's reserve.
    if (bankCredit.remainderCents > inheritedForPeriod + requirement.coveredCents + settlement.reimbursementsSettledCents + Math.max(0,paymentTimingCents) - amountCents) throw new Error("No se pudo conciliar la reserva liberada por la bonificación.");
    paidSnapshot.cardPayments = paidSnapshot.cardPayments.map(item=>item.name===card?{...item,amountCents:item.amountCents-bankCredit.remainderCents}:item);
    const remainingInheritedCents = Math.max(0, inheritedForPeriod + requirement.coveredCents + settlement.reimbursementsSettledCents - amountCents - bankCredit.remainderCents);
    paidSnapshot.cardPayments = paidSnapshot.cardPayments.map((item) => item.name === card && item.unassignedPeriod === period
      ? { ...item, unassignedCents: remainingInheritedCents, unassignedPeriod: remainingInheritedCents ? period : undefined }
      : item);
    const settledById = new Map(settlement.settled.map((movement) => [movement.id, movement]));
    settlement.settled.forEach((movement, index) => transaction.set(movementReferences[index], movement));
    const createdAt = nowIso();
    const result = {
      ok: true,
      tarjeta: card,
      periodo: period,
      pago: money(amountCents),
      reservaAnterior: money(reserved),
      reservaTrasReembolsos: money(reservedAfterReimbursements),
      reservaRestante: money(reservedAfterReimbursements - amountCents - bankCredit.remainderCents),
      bonificacionAplicada: money(bankCredit.totalCents),
      ajusteEntreCortes: money(paymentTimingCents),
      liberadoSinClasificar: money(bankCredit.remainderCents),
      movimientosPagados: settlement.settled.length,
      reembolsosConfirmados: money(settlement.reimbursementsSettledCents),
      cambioNetoNu: money(settlement.reimbursementsSettledCents - amountCents),
    };
    transaction.set(paymentEventReference, {
      id: paymentEventId,
      operationId,
      card,
      period,
      closedPeriod: period,
      amountCents,
      spentOn: paidOn,
      reservedBeforeCents: reserved,
      reservedAfterReimbursementsCents: reservedAfterReimbursements,
      reservedAfterCents: reservedAfterReimbursements - amountCents - bankCredit.remainderCents,
      bankCreditCents: bankCredit.totalCents,
      paymentTimingCents,
      releasedToUnclassifiedCents: bankCredit.remainderCents,
      nuDeltaCents: settlement.reimbursementsSettledCents - amountCents,
      settledMovementIds: settlement.settled.map((movement) => movement.id),
      reimbursementsSettledCents: settlement.reimbursementsSettledCents,
      createdAt,
      origin: "app",
    });
    transaction.set(operationReference, { id: operationId, kind: "card_payment", createdAt, result });
    return {
      snapshot: {
        ...paidSnapshot,
        movements: snapshot.movements.map((movement) => settledById.get(movement.id) ?? movement),
      },
      result,
    };
  });
}

async function maintenanceDetail(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const [{ doc, getDoc }, { firestore }] = await Promise.all([import("firebase/firestore"), import("./firebase")]);
  const [document, snapshot] = await Promise.all([
    getDoc(doc(firestore, "users", uid, "movements", String(payload.id))),
    readSnapshot(uid),
  ]);
  if (!document.exists()) throw new Error("El movimiento ya no existe.");
  const movement = document.data() as Movement;
  const historical = !isMovementInCurrentPeriod(snapshot, movement);
  const settled = isSettledMovement(movement);
  const linkedSavings = hasLinkedSavings(snapshot, movement);
  const installmentProtected = Boolean(movement.installmentPurchaseId);
  const protectedAmounts = installmentProtected || historical || settled || Boolean(movement.reimbursementReceived) || (movement.laterContributionCents ?? 0) > 0;
  const protectedDeletion = protectedAmounts || linkedSavings;
  const reason = settled
    ? "Este movimiento pertenece a un corte ya pagado."
    : installmentProtected ? "Esta parcialidad pertenece a un plan MSI y sus importes están protegidos."
      : historical ? "Este movimiento pertenece a una quincena cerrada."
      : movement.reimbursementReceived ? "Este movimiento ya recibió un reembolso."
        : (movement.laterContributionCents ?? 0) > 0 ? "Este movimiento tiene abonos posteriores." : "";
  return { ...movement, gasto: money(movement.billedAmountCents ?? movement.amountCents), reembolso: money(movement.reimbursementCents ?? 0), descripcion: movement.description ?? movement.merchant, categoria: movement.category, medioPago: movement.paymentMethod, fecha: movement.spentOn, fijo: movement.fixedExpense || movement.budgetImpact === false ? "Sí" : "No", periodo: movement.cardPeriod, periodoContexto: historical ? movement.periodId : snapshot.periodLabel, reembolsadoAhora: movement.reimbursementReceived, abonoPosterior: money(movement.laterContributionCents ?? 0), ahorroLigado: linkedSavings, restricciones: { medioPago: true, fecha: true, importes: protectedAmounts, eliminar: protectedDeletion, reembolso: settled }, motivoRestriccion: reason };
}

async function editMovement(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const movementId = String(payload.id);
  return mutateSnapshot(uid, async (snapshot, transaction, documentRef) => {
    const reference = documentRef("movements", movementId);
    const movementDocument = await transaction.get(reference);
    if (!movementDocument.exists()) throw new Error("El movimiento ya no existe.");
    const old = movementDocument.data() as Movement;
    if (old.kind !== "expense") throw new Error("Sólo se pueden editar gastos desde este formulario.");
    if (old.bankCreditAppliedCents) throw new Error("La compra tiene una bonificación aplicada; requiere revisión conjunta.");
    if (old.correctionRevision) throw new Error("Usa Corregir compra para conservar el historial y las fuentes de esta corrección.");
    if (old.installmentPurchaseId) throw new Error("Las parcialidades MSI están protegidas para conservar el plan y sus cortes.");
    if (!isMovementInCurrentPeriod(snapshot, old)) throw new Error("No se puede editar un movimiento de una quincena cerrada.");
    if (isSettledMovement(old)) throw new Error("No se puede editar un movimiento de un corte ya pagado.");
    if (String(payload.fecha || old.spentOn) !== String(old.spentOn ?? "")) throw new Error("La fecha es inmutable porque define la semana y el periodo de corte.");
    const billed = cents(payload.gasto); const reimbursement = cents(payload.reembolso);
    if (billed <= 0 || reimbursement < 0 || reimbursement > billed) throw new Error("Revisa el gasto y el reembolso.");
    const fixed = String(payload.fijo) === "Sí";
    const amountsChanged = billed !== (old.billedAmountCents ?? old.amountCents)
      || reimbursement !== Math.max(0, old.reimbursementCents ?? 0)
      || fixed !== Boolean(old.fixedExpense || old.budgetImpact === false);
    if (amountsChanged && (old.reimbursementReceived || (old.laterContributionCents ?? 0) > 0)) {
      throw new Error("Los importes están protegidos por dinero vinculado. Sólo puedes corregir descripción o categoría.");
    }
    const description = String(payload.descripcion ?? "").trim();
    if (!description) throw new Error("Escribe una descripción para el movimiento.");
    const plan = planMovementEdit(snapshot, old, { billedAmountCents: billed, reimbursementCents: reimbursement, fixedExpense: fixed });
    if (!plan.allowed) {
      if (plan.reason === "insufficient_savings") throw new Error(`Aun usando el ahorro libre, faltan ${money(plan.missingCents ?? 0).toFixed(2)}.`);
      throw new Error("No se pudo calcular la corrección para la semana de este movimiento.");
    }
    if (plan.savingsRescueCents > 0) {
      if (payload.confirmadoRescateAhorro !== true) throw new Error(`La semana no alcanza. Confirma usar ${money(plan.savingsRescueCents).toFixed(2)} del ahorro libre.`);
      if (cents(payload.rescateAhorro) !== plan.savingsRescueCents) throw new Error("Los saldos cambiaron mientras confirmabas. Revisa nuevamente la corrección.");
    }
    const nextReal = plan.nextRealCents; const realDelta = nextReal - old.amountCents;
    const provisional: Movement = { ...old, amountCents: nextReal, billedAmountCents: billed, reimbursementCents: reimbursement, fixedExpense: fixed, budgetImpact: !fixed };
    const oldAccount = inferredAccountDelta(old);
    const newAccount = editedAccountDeltaCents(old, oldAccount, billed, reimbursement);
    const nextCovered = movementCoveredCents(provisional);
    const nextPending = Math.max(0, billed - nextCovered);
    const oldCovered = Math.min(old.billedAmountCents ?? old.amountCents, Math.max(0, old.coveredAmountCents ?? movementCoveredCents(old)));
    const reserveDelta = isTrackedCard(old.paymentMethod) ? nextCovered - oldCovered : 0;
    const next: Movement = { ...provisional, merchant: description, description, category: String(payload.categoria), spentOn: old.spentOn, coveredAmountCents: nextCovered, pendingAmountCents: nextPending, cardStatus: nextPending > 0 ? (nextCovered > 0 ? "Parcial" : "Pendiente") : "Cubierto", weeklyImpactCents: plan.nextWeeklyImpactCents, savingsRescueCents: Math.max(0, old.savingsRescueCents ?? 0) + plan.savingsRescueCents, accountDeltaCents: newAccount, updatedAt: nowIso(), syncState: "synced" };
    transaction.set(reference, next);
    let categorySpend = categoryWithDelta(snapshot, old.category, -old.amountCents);
    categorySpend = categoryWithDelta({ ...snapshot, categorySpend }, next.category, next.amountCents);
    const weeks = snapshot.weeks.map((item) => {
      if (item.id !== old.weekId) return item;
      let weeklyCategories = categoryWithDelta({ ...snapshot, categorySpend: item.categorySpend ?? [] }, old.category, -plan.oldOwnWeeklyCents);
      weeklyCategories = categoryWithDelta({ ...snapshot, categorySpend: weeklyCategories }, next.category, plan.nextOwnWeeklyCents);
      return { ...item, availableCents: item.availableCents - plan.weekDeltaCents + plan.savingsRescueCents, spentCents: Math.max(0, item.spentCents + plan.weekDeltaCents), categorySpend: weeklyCategories };
    });
    let updated = replaceMovement({ ...snapshot, accounts: accountWithDelta(snapshot, newAccount - oldAccount), weeks, savingsCents: snapshot.savingsCents - plan.savingsRescueCents, cardPayments: updateCardReserve(snapshot, old.paymentMethod, reserveDelta), totalSpentCents: Math.max(0, snapshot.totalSpentCents + realDelta), categorySpend }, next);
    if (plan.savingsRescueCents > 0) {
      const rescueId = `RESCATE_EDICION_${safeDocumentId(payload.accionId, movementId)}`;
      const rescue: Movement = { id: rescueId, periodId: snapshot.periodLabel, kind: "savings_rescue", amountCents: plan.savingsRescueCents, merchant: `Completar corrección de ${description}`, description: `Diferencia para corregir ${description}`, paymentMethod: "Ahorro → semana", weekId: old.weekId, category: "Ahorro", spentOn: old.spentOn, createdAt: nowIso(), syncState: "synced", relatedMovementId: old.id, savingsBucketId: "DISPONIBLE", savingsSource: "Ahorro → semana", origin: "app", schemaVersion: 3 };
      transaction.set(documentRef("movements", rescueId), rescue);
      updated = { ...updated, movements: [rescue, ...updated.movements.filter((movement) => movement.id !== rescueId)].slice(0, 200) };
    }
    return { snapshot: updated, result: { ok: true, ahorroConservado: money(plan.persistentSavingsCents), rescateAhorro: money(plan.savingsRescueCents) } };
  });
}

async function deleteMovement(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const movementId = String(payload.id);
  return mutateSnapshot(uid, async (snapshot, transaction, documentRef) => {
    const reference = documentRef("movements", movementId);
    const movementDocument = await transaction.get(reference);
    if (!movementDocument.exists()) throw new Error("El movimiento ya no existe.");
    const movement = movementDocument.data() as Movement;
    if (movement.kind !== "expense") throw new Error("Sólo se pueden eliminar gastos desde este formulario.");
    if (movement.bankCreditAppliedCents) throw new Error("La compra tiene una bonificación aplicada; requiere revisión conjunta.");
    if (movement.correctionRevision) throw new Error("Una compra corregida conserva su historial. Usa Corregir compra para ajustar sus datos.");
    if (movement.installmentPurchaseId) throw new Error("No se puede eliminar una sola parcialidad porque rompería el plan MSI.");
    if (!isMovementInCurrentPeriod(snapshot, movement)) throw new Error("No se puede eliminar un movimiento de una quincena cerrada.");
    if (isSettledMovement(movement)) throw new Error("No se puede eliminar un movimiento de un corte ya pagado.");
    if (hasLinkedSavings(snapshot, movement)) throw new Error("No se puede eliminar porque tiene ahorro o un rescate vinculado.");
    if (movement.reimbursementReceived || (movement.laterContributionCents ?? 0) > 0) throw new Error("No se puede eliminar porque tiene dinero posterior vinculado.");
    const weekly = movement.weeklyImpactCents ?? ownCoveredByWeek(movement);
    const covered = isTrackedCard(movement.paymentMethod) ? Math.max(0, movement.coveredAmountCents ?? 0) : 0;
    transaction.delete(reference);
    if (movement.receiptAttachmentId) transaction.delete(documentRef("receipts", movement.receiptAttachmentId));
    return { snapshot: { ...snapshot, accounts: accountWithDelta(snapshot, -inferredAccountDelta(movement)), weeks: snapshot.weeks.map((item) => item.id === movement.weekId ? { ...item, availableCents: item.availableCents + weekly, spentCents: Math.max(0, item.spentCents - weekly) } : item), cardPayments: updateCardReserve(snapshot, movement.paymentMethod, -covered), totalSpentCents: Math.max(0, snapshot.totalSpentCents - movement.amountCents), categorySpend: categoryWithDelta(snapshot, movement.category, -movement.amountCents), movements: snapshot.movements.filter((item) => item.id !== movementId) } };
  });
}

async function correctReimbursement(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const movementId = String(payload.id);
  const operationId = safeDocumentId(payload.accionId, "reimbursement_correction");
  return mutateSnapshot(uid, async (snapshot, transaction, documentRef) => {
    const reference = documentRef("movements", movementId);
    const operationReference = documentRef("operations", operationId);
    const [movementDocument, operationDocument] = await Promise.all([transaction.get(reference), transaction.get(operationReference)]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (!movementDocument.exists()) throw new Error("El movimiento ya no existe.");
    const old = movementDocument.data() as Movement;
    if (old.kind !== "expense") throw new Error("El movimiento no admite reembolsos.");
    if (old.bankCreditAppliedCents) throw new Error("La compra tiene una bonificación aplicada; requiere revisión conjunta.");
    if (old.correctionRevision) throw new Error("Corrige el reembolso desde Corregir compra para conservar todas sus fuentes.");
    if (old.installmentPurchaseId) throw new Error("Los reembolsos de un plan MSI se administran por parcialidad desde Pendientes.");
    if (isSettledMovement(old)) throw new Error("No se puede corregir un reembolso de un corte ya pagado.");
    if ((old.laterContributionCents ?? 0) > 0) throw new Error("El movimiento tiene abonos posteriores y está protegido.");
    const oldReimbursement = Math.max(0, old.reimbursementCents ?? 0);
    const nextReimbursement = String(payload.tipo) === "CORREGIR_IMPORTE" ? cents(payload.importe) : oldReimbursement;
    const received = String(payload.tipo) === "CORREGIR_IMPORTE";
    const billed = old.billedAmountCents ?? old.amountCents + oldReimbursement;
    if (nextReimbursement < 0 || nextReimbursement > billed) throw new Error("El reembolso no puede superar el gasto total.");
    if (!isMovementInCurrentPeriod(snapshot, old) && nextReimbursement !== oldReimbursement) throw new Error("En una quincena cerrada sólo puedes cambiar el estado del reembolso, no su importe.");
    if (nextReimbursement !== oldReimbursement && hasLinkedSavings(snapshot, old)) throw new Error("El importe está protegido porque el gasto tiene ahorro vinculado.");
    const receivedDelta = (received ? nextReimbursement : 0) - (old.reimbursementReceived ? oldReimbursement : 0);
    const source = reimbursementFundingSourceFromMovement(old);
    let contributionBefore: TemporaryContribution | undefined;
    if (receivedDelta && source.kind === "fund") {
      if (!source.sourceId) throw new Error("El movimiento no conserva el fondo original del reembolso.");
      const contributionDocument = await transaction.get(documentRef("temporaryContributions", source.sourceId));
      if (!contributionDocument.exists()) throw new Error("El fondo original del reembolso ya no existe.");
      contributionBefore = { id: contributionDocument.id, ...contributionDocument.data() } as TemporaryContribution;
    }
    const funding = applyReimbursementFundingChange(snapshot, receivedDelta, source, contributionBefore);
    const realDelta = (oldReimbursement - nextReimbursement);
    const nextReal = Math.max(0, billed - nextReimbursement);
    const oldWeekly = old.weeklyImpactCents ?? ownCoveredByWeek(old);
    const provisional = { ...old, amountCents: nextReal, reimbursementCents: nextReimbursement, reimbursementReceived: received };
    const nextWeekly = old.budgetImpact === false || old.fixedExpense ? 0 : ownCoveredByWeek(provisional) + Math.max(0, old.savingsContributionCents ?? 0);
    const weekDelta = nextWeekly - oldWeekly;
    const nextCovered = movementCoveredCents(provisional);
    const nextPending = Math.max(0, billed - nextCovered);
    const oldCovered = Math.min(billed, Math.max(0, old.coveredAmountCents ?? movementCoveredCents(old)));
    const reserveDelta = isTrackedCard(old.paymentMethod) ? nextCovered - oldCovered : 0;
    const createdAt = nowIso();
    const next: Movement = { ...provisional, coveredAmountCents: nextCovered, pendingAmountCents: nextPending, weeklyImpactCents: nextWeekly, accountDeltaCents: inferredAccountDelta(old) + funding.accountDeltaCents, cardStatus: nextPending ? (nextCovered > 0 ? "Parcial" : "Pendiente") : "Cubierto", updatedAt: createdAt, syncState: "synced" };
    transaction.set(reference, next);
    const ownWeek = funding.snapshot.weeks.find((item) => item.id === old.weekId);
    if (weekDelta > 0 && (!ownWeek || ownWeek.availableCents < weekDelta)) {
      throw new Error("La semana del movimiento no tiene saldo suficiente para aumentar tu parte.");
    }
    if (weekDelta < 0 && (!ownWeek || ownWeek.spentCents < -weekDelta)) {
      throw new Error("La semana ya no conserva el uso necesario para corregir este importe.");
    }
    let correctedSnapshot: FinancialSnapshot = {
      ...funding.snapshot,
      weeks: funding.snapshot.weeks.map((item) => item.id === old.weekId ? { ...item, availableCents: item.availableCents - weekDelta, spentCents: Math.max(0, item.spentCents + weekDelta) } : item),
      cardPayments: updateCardReserve(funding.snapshot, old.paymentMethod, reserveDelta),
      totalSpentCents: Math.max(0, funding.snapshot.totalSpentCents + realDelta),
      categorySpend: categoryWithDelta(funding.snapshot, old.category, realDelta),
    };
    let sourceResult: Record<string, unknown> = {};
    if (receivedDelta && source.kind === "week" && !funding.restoredToUnclassifiedNu) {
      const sourceWeek = correctedSnapshot.weeks.find((week) => week.id === source.sourceId);
      if (sourceWeek) {
        const periodId = slug(String(correctedSnapshot.periodLabel ?? "period"));
        const eventId = `reimbursement_source_${operationId}`;
        transaction.set(documentRef("weeks", `${periodId}_${sourceWeek.id}`), { ...sourceWeek, periodId, updatedAt: createdAt, origin: "app" }, { merge: true });
        transaction.set(documentRef("weeklyAdjustments", eventId), { id: eventId, type: "reimbursement_correction", periodId, weekId: sourceWeek.id, amountCents: Math.abs(receivedDelta), direction: receivedDelta > 0 ? "allocate" : "return", movementId, card: old.paymentMethod, createdAt, origin: "app" });
      }
    }
    if (receivedDelta && source.kind === "savings") {
      const sourceMovement: Movement = {
        id: `reimbursement_source_${operationId}`,
        periodId: correctedSnapshot.periodLabel,
        kind: receivedDelta > 0 ? "savings_withdrawal" : "savings_contribution",
        amountCents: Math.abs(receivedDelta),
        merchant: "Corrección de reembolso",
        description: `${receivedDelta > 0 ? "Ajuste para" : "Devolución de"} ${old.description ?? old.merchant}`,
        paymentMethod: old.paymentMethod,
        relatedMovementId: movementId,
        savingsBucketId: "DISPONIBLE",
        savingsSource: `Reserva de ${old.paymentMethod}`,
        spentOn: dateOnly(),
        createdAt,
        syncState: "synced",
        origin: "app",
        schemaVersion: 3,
      };
      transaction.set(documentRef("movements", sourceMovement.id), sourceMovement);
      correctedSnapshot = { ...correctedSnapshot, movements: [sourceMovement, ...correctedSnapshot.movements].slice(0, 200) };
    }
    if (receivedDelta && source.kind === "fund" && contributionBefore && funding.contribution) {
      const updatedContribution: TemporaryContribution = { ...funding.contribution, updatedAt: createdAt };
      const event: TemporaryContributionEvent = {
        id: `event_reimbursement_source_${operationId}`,
        contributionId: updatedContribution.id,
        type: "reimbursement_allocation",
        amountDeltaCents: -receivedDelta,
        beforeBalanceCents: contributionBefore.balanceCents,
        afterBalanceCents: updatedContribution.balanceCents,
        descriptionBefore: contributionBefore.description,
        descriptionAfter: updatedContribution.description,
        accountDeltaCents: 0,
        note: `${receivedDelta > 0 ? "Ajuste para" : "Devolución de"} reembolso de ${old.description ?? old.merchant}`,
        createdAt,
        origin: "app",
      };
      transaction.set(documentRef("temporaryContributions", updatedContribution.id), updatedContribution, { merge: true });
      transaction.set(documentRef("temporaryContributionEvents", event.id), event);
      sourceResult = { fondosActualizados: [updatedContribution], eventosDeFondos: [event] };
    }
    const result = { ok: true, movimientoId: movementId, tipo: String(payload.tipo), fuente: source.kind, devueltoSinClasificar: funding.restoredToUnclassifiedNu, ...sourceResult };
    transaction.set(operationReference, { id: operationId, kind: "maintenance_reimbursement", createdAt, result });
    return { snapshot: replaceMovement(correctedSnapshot, next), result };
  });
}

async function readTemporaryContributions(uid: string): Promise<TemporaryContribution[]> {
  const [{ collection, getDocs }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const documents = await getDocs(collection(firestore, "users", uid, "temporaryContributions"));
  return documents.docs
    .map((document) => ({ id: document.id, ...document.data() }) as TemporaryContribution)
    .filter((item) => item.active !== false)
    .sort((a, b) => Number(a.order) - Number(b.order));
}

async function temporaryContribution(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const description = String(payload.descripcion ?? "").trim();
  const amountCents = cents(payload.monto);
  if (!description) throw new Error("Escribe una descripción para identificar este dinero.");
  if (amountCents <= 0) throw new Error("El importe debe ser mayor que cero.");
  const operationId = safeDocumentId(payload.operacionId, "aporte");
  const contributionId = safeDocumentId(payload.aportacionId, `aporte_${operationId}`);
  if (contributionId === YIELD_FUND_ID) throw new Error("El fondo de rendimientos se configura desde Actualizar rendimientos.");
  const alreadyIncludedInNu = payload.yaIncluidoEnNu === true;
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const contributionReference = document("temporaryContributions", contributionId);
    const [operationDocument, existingContribution] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(contributionReference),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (existingContribution.exists()) throw new Error("Esta aportación ya existe. Actualiza antes de intentarlo otra vez.");
    const createdAt = nowIso();
    const contribution: TemporaryContribution = {
      id: contributionId,
      description,
      balanceCents: amountCents,
      originalAmountCents: amountCents,
      note: String(payload.nota ?? "").trim(),
      active: true,
      order: Date.now(),
      createdAt,
      updatedAt: createdAt,
      origin: "app",
    };
    const event: TemporaryContributionEvent = {
      id: `event_${operationId}`,
      contributionId,
      type: "create",
      amountDeltaCents: amountCents,
      beforeBalanceCents: 0,
      afterBalanceCents: amountCents,
      descriptionBefore: description,
      descriptionAfter: description,
      accountDeltaCents: alreadyIncludedInNu ? 0 : amountCents,
      note: contribution.note,
      createdAt,
      origin: "app",
    };
    const result = { ok: true, aportacionId: contributionId, descripcion: description, saldo: money(amountCents), nuAumentado: !alreadyIncludedInNu };
    transaction.set(contributionReference, contribution);
    transaction.set(document("temporaryContributionEvents", event.id), event);
    transaction.set(operationReference, { id: operationId, kind: "temporary_contribution", createdAt, result });
    return {
      snapshot: {
        ...snapshot,
        accounts: alreadyIncludedInNu ? snapshot.accounts : accountWithDelta(snapshot, amountCents),
        temporaryContributionCents: snapshot.temporaryContributionCents + amountCents,
      },
      result,
    };
  });
}

async function manageTemporaryContribution(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const contributionId = safeDocumentId(payload.aportacionId, "aporte");
  if (contributionId === YIELD_FUND_ID) throw new Error("Los rendimientos se actualizan desde su propio editor.");
  const operationId = safeDocumentId(payload.operacionId, "aporte_accion");
  const action = String(payload.accion ?? "").toUpperCase();
  if (!["AJUSTAR", "RENOMBRAR", "CANCELAR"].includes(action)) throw new Error("La acción sobre la aportación no es válida.");

  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const contributionReference = document("temporaryContributions", contributionId);
    const [operationDocument, contributionDocument] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(contributionReference),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    const legacyTotal = contributionId === "legacy-temporary-total" && !contributionDocument.exists();
    if (!contributionDocument.exists() && !legacyTotal) throw new Error("La aportación ya no existe o cambió.");
    if (legacyTotal && snapshot.temporaryContributionCents <= 0) throw new Error("El fondo migrado ya no tiene saldo disponible.");

    const contribution = legacyTotal
      ? { id: contributionId, description: "Aporte temporal migrado", balanceCents: snapshot.temporaryContributionCents, originalAmountCents: snapshot.temporaryContributionCents, active: true, order: 0, origin: "migration" as const }
      : { id: contributionDocument.id, ...contributionDocument.data() } as TemporaryContribution;
    const beforeDescription = contribution.description;
    const beforeBalanceCents = contribution.balanceCents;
    const change = action === "RENOMBRAR"
      ? { type: "rename" as const, description: String(payload.descripcion ?? "") }
      : action === "CANCELAR"
        ? { type: "cancel" as const }
        : { type: "adjustment" as const, deltaCents: cents(payload.monto) * (String(payload.tipo ?? "SUMAR").toUpperCase() === "RESTAR" ? -1 : 1) };
    const changed = applyTemporaryContributionChange(snapshot.temporaryContributionCents, contribution, change);
    const adjustNu = action !== "RENOMBRAR" && payload.ajustarNu === true;
    const accountDeltaCents = adjustNu ? changed.amountDeltaCents : 0;
    const nuBalanceCents = snapshot.accounts[0]?.balanceCents ?? 0;
    if (nuBalanceCents + accountDeltaCents < 0) throw new Error("El ajuste dejaría el saldo de Nu por debajo de cero.");

    const createdAt = nowIso();
    const updated: TemporaryContribution = { ...changed.contribution, updatedAt: createdAt };
    const eventType: TemporaryContributionEvent["type"] = action === "RENOMBRAR" ? "rename" : action === "CANCELAR" ? "cancel" : "adjustment";
    const event: TemporaryContributionEvent = {
      id: `event_${operationId}`,
      contributionId,
      type: eventType,
      amountDeltaCents: changed.amountDeltaCents,
      beforeBalanceCents,
      afterBalanceCents: updated.balanceCents,
      descriptionBefore: beforeDescription,
      descriptionAfter: updated.description,
      accountDeltaCents,
      note: String(payload.nota ?? "").trim(),
      createdAt,
      origin: "app",
    };
    const result = {
      ok: true,
      accion: action,
      aportacionId: contributionId,
      descripcion: updated.description,
      saldoAnterior: money(beforeBalanceCents),
      saldoNuevo: money(updated.balanceCents),
      aporteTemporalNeto: money(changed.totalCents),
      cambioEnNu: money(accountDeltaCents),
    };
    transaction.set(contributionReference, updated, { merge: true });
    transaction.set(document("temporaryContributionEvents", event.id), event);
    transaction.set(operationReference, { id: operationId, kind: "temporary_contribution_manage", createdAt, result });
    return {
      snapshot: {
        ...snapshot,
        accounts: accountDeltaCents ? accountWithDelta(snapshot, accountDeltaCents) : snapshot.accounts,
        temporaryContributionCents: changed.totalCents,
      },
      result,
    };
  });
}

async function weekToFund(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const fundId = safeDocumentId(payload.aportacionId, "fondo");
  const operationId = safeDocumentId(payload.operacionId, "semana_fondo");
  const amountCents = cents(payload.monto);
  const periodId = String(payload.periodo ?? "");
  const weekId = String(payload.semana ?? "") as WeekId;
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const fundReference = document("temporaryContributions", fundId);
    const [operation, fundDocument] = await Promise.all([transaction.get(operationReference), transaction.get(fundReference)]);
    if (operation.exists()) return { snapshot, result: operation.data().result, skipSnapshotWrite: true };
    const legacy = fundId === "legacy-temporary-total" && !fundDocument.exists();
    if (!fundDocument.exists() && !legacy) throw new Error("El fondo ya no existe. Actualiza la información.");
    const fund = legacy ? { id: fundId, description: "Aporte temporal migrado", balanceCents: snapshot.temporaryContributionCents, originalAmountCents: snapshot.temporaryContributionCents, active: true, order: 0, origin: "migration" as const }
      : { ...fundDocument.data(), id: fundId } as TemporaryContribution;
    const applied = applyWeekToFund(snapshot, fund, amountCents, weekId, periodId);
    const createdAt = nowIso();
    const week = snapshot.weeks.find(item => item.id === weekId)!;
    const movement: Movement = { id: operationId, periodId, weekId, kind: "week_adjustment", amountCents, merchant: "Semana a fondo", description: `${week.label} → ${fund.description}`, paymentMethod: "Reclasificación en Nu", accountDeltaCents: 0, createdAt, spentOn: dateOnly(), syncState: "synced", origin: "app", schemaVersion: 3 };
    transaction.set(fundReference, { ...applied.fund, updatedAt: createdAt });
    transaction.set(document("temporaryContributionEvents", `event_${operationId}`), { id: `event_${operationId}`, contributionId: fundId, type: "week_allocation", amountDeltaCents: amountCents, beforeBalanceCents: fund.balanceCents, afterBalanceCents: applied.fund.balanceCents, descriptionBefore: fund.description, descriptionAfter: fund.description, accountDeltaCents: 0, periodLabel: periodId, relatedDescription: week.label, note: String(payload.nota ?? "").trim(), createdAt, origin: "app" });
    transaction.set(document("weeklyAdjustments", operationId), { id: operationId, type: "week_to_fund", weekId, periodId, amountCents, source: "week", contributionId: fundId, accountDeltaCents: 0, createdAt, origin: "app" });
    transaction.set(document("movements", operationId), movement);
    const result = { ok: true, fondo: fundId, monto: amountCents / 100 };
    transaction.set(operationReference, { id: operationId, kind: "week_to_fund", createdAt, result });
    return { snapshot: { ...applied.snapshot, movements: [movement, ...applied.snapshot.movements].slice(0, 200) }, result };
  });
}

async function transferTemporaryContribution(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const contributionId = safeDocumentId(payload.aportacionId, "aporte");
  const operationId = safeDocumentId(payload.operacionId, "aporte_destino");
  const amountCents = cents(payload.monto);
  const requestedDestination = String(payload.destino ?? "").toLowerCase();
  if (!(["week", "savings", "fund", "outside", "classification"] as string[]).includes(requestedDestination)) {
    throw new Error("Elige a dónde quieres mover el dinero.");
  }
  const destination = requestedDestination as TemporaryContributionDestination;
  const destinationContributionId = destination === "fund"
    ? safeDocumentId(payload.fondoDestinoId, "fondo receptor")
    : "";
  if (destination === "fund" && destinationContributionId === contributionId) {
    throw new Error("Elige otro fondo como receptor.");
  }
  if (destination === "fund" && destinationContributionId === YIELD_FUND_ID) {
    throw new Error("El fondo de rendimientos sólo recibe nuevas ganancias registradas desde Nu.");
  }

  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const contributionReference = document("temporaryContributions", contributionId);
    const destinationContributionReference = destination === "fund"
      ? document("temporaryContributions", destinationContributionId)
      : undefined;
    const [operationDocument, contributionDocument, destinationContributionDocument] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(contributionReference),
      destinationContributionReference ? transaction.get(destinationContributionReference) : Promise.resolve(undefined),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };

    const legacyTotal = contributionId === "legacy-temporary-total" && !contributionDocument.exists();
    if (!contributionDocument.exists() && !legacyTotal) throw new Error("El fondo ya no existe o cambió.");
    if (destination === "fund" && !destinationContributionDocument?.exists()) {
      throw new Error("El fondo receptor ya no existe o cambió.");
    }
    const contribution = legacyTotal
      ? { id: contributionId, description: "Aporte temporal migrado", balanceCents: snapshot.temporaryContributionCents, originalAmountCents: snapshot.temporaryContributionCents, active: true, order: 0, origin: "migration" as const }
      : { id: contributionDocument.id, ...contributionDocument.data() } as TemporaryContribution;
    const destinationContribution = destinationContributionDocument?.exists()
      ? { id: destinationContributionDocument.id, ...destinationContributionDocument.data() } as TemporaryContribution
      : undefined;
    const applied = applyTemporaryContributionDestination(snapshot, contribution, amountCents, destination, destinationContribution);
    const createdAt = nowIso();
    const updatedContribution: TemporaryContribution = { ...applied.contribution, updatedAt: createdAt };
    const updatedDestinationContribution: TemporaryContribution | undefined = applied.destinationContribution
      ? { ...applied.destinationContribution, updatedAt: createdAt }
      : undefined;
    const eventType: TemporaryContributionEvent["type"] = destination === "week"
      ? "week_allocation"
      : destination === "savings"
        ? "savings_allocation"
        : destination === "fund"
          ? "fund_transfer"
          : destination === "outside" ? "external_withdrawal" : "classification_correction";
    const destinationLabel = destination === "week"
      ? activeWeek(applied.snapshot).label
      : destination === "savings"
        ? "Ahorro disponible"
        : destination === "fund"
          ? `Fondo disponible: ${updatedDestinationContribution?.description ?? "receptor"}`
          : destination === "outside" ? "Fuera de Banculem" : "Corrección de clasificación";
    const note = String(payload.nota ?? "").trim();
    const event: TemporaryContributionEvent = {
      id: destination === "fund" ? `event_${operationId}_source` : `event_${operationId}`,
      contributionId,
      type: eventType,
      destination,
      amountDeltaCents: -amountCents,
      beforeBalanceCents: contribution.balanceCents,
      afterBalanceCents: updatedContribution.balanceCents,
      descriptionBefore: contribution.description,
      descriptionAfter: updatedContribution.description,
      accountDeltaCents: applied.accountDeltaCents,
      relatedContributionId: updatedDestinationContribution?.id,
      relatedDescription: updatedDestinationContribution?.description,
      note,
      periodLabel: snapshot.periodLabel,
      createdAt,
      origin: "app",
    };

    let next = applied.snapshot;
    let movement: Movement | undefined;
    if (destination === "week") {
      const week = activeWeek(next);
      movement = {
        id: operationId,
        periodId: snapshot.periodLabel,
        kind: "week_adjustment",
        amountCents,
        merchant: `Fondo disponible a ${week.label}`,
        description: note || contribution.description,
        paymentMethod: "Fondo disponible",
        weekId: week.id,
        accountDeltaCents: 0,
        weekFundingSource: "temporary_contribution",
        temporaryAllocationIds: [contributionId],
        createdAt,
        spentOn: dateOnly(),
        syncState: "synced",
        origin: "app",
        schemaVersion: 3,
      };
      const periodId = slug(String(snapshot.periodLabel ?? "period"));
      next.weeks.forEach((item) => transaction.set(document("weeks", `${periodId}_${item.id}`), { ...item, periodId, updatedAt: createdAt, origin: "app" }, { merge: true }));
      transaction.set(document("weeklyAdjustments", operationId), { id: operationId, type: "funding", periodId, weekId: week.id, amountCents, source: "temporary_contribution", temporaryAllocations: [{ id: contributionId, description: contribution.description, beforeCents: contribution.balanceCents, usedCents: amountCents, afterCents: updatedContribution.balanceCents }], accountDeltaCents: 0, createdAt, origin: "app" });
    } else if (destination === "savings") {
      movement = {
        id: operationId,
        periodId: snapshot.periodLabel,
        kind: "savings_contribution",
        amountCents,
        merchant: "Fondo disponible a ahorro",
        description: note || contribution.description,
        paymentMethod: "Fondo disponible",
        savingsBucketId: "DISPONIBLE",
        savingsSource: `Fondo disponible: ${contribution.description}`,
        accountDeltaCents: 0,
        createdAt,
        spentOn: dateOnly(),
        syncState: "synced",
        origin: "app",
        schemaVersion: 3,
      };
    }
    if (movement) {
      transaction.set(document("movements", operationId), movement);
      next = { ...next, movements: [movement, ...next.movements].slice(0, 200) };
    }

    const destinationEvent: TemporaryContributionEvent | undefined = updatedDestinationContribution && destinationContribution
      ? {
          id: `event_${operationId}_target`,
          contributionId: updatedDestinationContribution.id,
          type: "fund_transfer",
          destination: "fund",
          amountDeltaCents: amountCents,
          beforeBalanceCents: destinationContribution.balanceCents,
          afterBalanceCents: updatedDestinationContribution.balanceCents,
          descriptionBefore: destinationContribution.description,
          descriptionAfter: updatedDestinationContribution.description,
          accountDeltaCents: 0,
          relatedContributionId: contributionId,
          relatedDescription: contribution.description,
          note,
          periodLabel: snapshot.periodLabel,
          createdAt,
          origin: "app",
        }
      : undefined;
    const result = {
      ok: true,
      aportacionId: contributionId,
      monto: money(amountCents),
      destino: destination,
      destinoDescripcion: destinationLabel,
      saldoFondo: money(updatedContribution.balanceCents),
      cambioEnNu: money(applied.accountDeltaCents),
      fondosActualizados: [updatedContribution, ...(updatedDestinationContribution ? [updatedDestinationContribution] : [])],
      eventosDeFondos: [event, ...(destinationEvent ? [destinationEvent] : [])],
    };
    transaction.set(contributionReference, updatedContribution, { merge: true });
    if (destinationContributionReference && updatedDestinationContribution) {
      transaction.set(destinationContributionReference, updatedDestinationContribution, { merge: true });
    }
    transaction.set(document("temporaryContributionEvents", event.id), event);
    if (destinationEvent) transaction.set(document("temporaryContributionEvents", destinationEvent.id), destinationEvent);
    transaction.set(operationReference, { id: operationId, kind: "temporary_contribution_transfer", createdAt, result });
    return { snapshot: next, result };
  });
}

async function preparePeriod(uid: string): Promise<Record<string, unknown>> {
  const [snapshot, contributions] = await Promise.all([readSnapshot(uid), readTemporaryContributions(uid)]);
  const startDate = snapshot.periodEnd ? new Date(`${snapshot.periodEnd}T12:00:00`) : new Date();
  startDate.setDate(startDate.getDate() + 1);
  const start = [startDate.getFullYear(), String(startDate.getMonth() + 1).padStart(2, "0"), String(startDate.getDate()).padStart(2, "0")].join("-");
  const temporary = snapshot.temporaryContributionCents;
  const available = contributions.filter((item) => item.balanceCents > 0);
  const closingWeek = snapshot.weeks.find((week) => week.id === "week2" && week.status !== "upcoming")
    ?? snapshot.weeks.find((week) => week.status === "active");
  const previousCarryover = Math.max(0, snapshot.previousPeriodCarryover?.availableCents ?? 0);
  const closingWeekAvailable = Math.max(0, closingWeek?.availableCents ?? 0);
  return {
    ok: true,
    periodoOrigen: snapshot.periodLabel ?? "Quincena actual",
    inicioSugerido: start,
    finSugerido: periodEndFromStart(start),
    nombreSugerido: periodLabel(start),
    sobranteSemanaFinal: money(closingWeekAvailable),
    sobranteAnteriorNoUsado: money(previousCarryover),
    sobranteTotalTransferible: money(closingWeekAvailable + previousCarryover),
    aporteTemporalNeto: money(temporary),
    nominas: available.length
      ? available.map((item) => ({ id: item.id, concepto: item.description, saldo: money(item.balanceCents) }))
      : temporary > 0 ? [{ id: "legacy-temporary-total", concepto: "Aporte temporal migrado", saldo: money(temporary) }] : [],
  };
}

async function createPeriod(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const start = String(payload.inicio); const end = periodEndFromStart(start); const label = periodLabel(start); const baseCents = cents(payload.montoBase); const mode = Number(payload.modo);
  if (baseCents <= 0 || ![1, 2, 3].includes(mode)) throw new Error("Revisa el presupuesto y su origen.");
  const operationId = safeDocumentId(payload.operacionId, "period");
  const selectedContributionIds = [...new Set((Array.isArray(payload.aportaciones) ? payload.aportaciones : []).map(String))];
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    if (String(payload.periodoOrigen ?? snapshot.periodLabel ?? "") !== String(snapshot.periodLabel ?? "")) throw new Error("La quincena activa cambió. Vuelve a preparar el cierre.");
    if (snapshot.periodEnd && !isPeriodReadyToClose(snapshot.periodEnd, dateOnly())) throw new Error(`La quincena actual termina el ${snapshot.periodEnd}. Podrás cerrar y activar la siguiente a partir del día posterior.`);
    if (snapshot.periodEnd && start !== addIsoDays(snapshot.periodEnd, 1)) throw new Error("La nueva quincena debe comenzar exactamente al día siguiente de la actual.");
    if (mode === 1 && baseCents > snapshot.temporaryContributionCents) throw new Error("El aporte temporal neto no alcanza para ese presupuesto.");
    if (mode === 1 && !selectedContributionIds.length) throw new Error("Elige las aportaciones que formarán el presupuesto.");

    const periodId = slug(label);
    const previousPeriodId = slug(String(snapshot.periodLabel ?? "period"));
    const rolloverCreatedAt = nowIso();
    const rollover = carryoverAtPeriodClose(snapshot, label, `rollover_${previousPeriodId}_${periodId}`, rolloverCreatedAt);
    const operationReference = document("operations", operationId);
    const periodReference = document("periods", periodId);
    const contributionReferences = mode === 1
      ? selectedContributionIds.filter((id) => id !== "legacy-temporary-total").map((id) => document("temporaryContributions", id))
      : [];
    const [operationDocument, existingPeriod, ...contributionDocuments] = await Promise.all([
      transaction.get(operationReference),
      transaction.get(periodReference),
      ...contributionReferences.map((reference) => transaction.get(reference)),
    ]);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    if (existingPeriod.exists()) throw new Error(`Ya existe la quincena “${label}”. No se duplicó nada.`);

    let allocations: ReturnType<typeof allocateTemporaryContributions>["allocations"] = [];
    if (mode === 1) {
      if (selectedContributionIds.includes("legacy-temporary-total")) {
        if (selectedContributionIds.length !== 1) throw new Error("Actualiza la lista de aportaciones antes de continuar.");
        allocations = [{ id: "legacy-temporary-total", description: "Aporte temporal migrado", beforeCents: snapshot.temporaryContributionCents, usedCents: baseCents, afterCents: snapshot.temporaryContributionCents - baseCents }];
      } else {
        const selected = contributionDocuments.map((item, index) => {
          if (!item.exists()) throw new Error("Una aportación seleccionada cambió o ya no existe.");
          return { id: contributionReferences[index].id, ...item.data() } as TemporaryContribution;
        });
        const selectedTotal = selected.reduce((sum, item) => sum + item.balanceCents, 0);
        const balancingEntry: TemporaryContribution = { id: "unselected-net-balance", description: "Otros fondos temporales", balanceCents: snapshot.temporaryContributionCents - selectedTotal, active: true, order: Number.MAX_SAFE_INTEGER };
        const allocation = allocateTemporaryContributions([...selected, balancingEntry], selectedContributionIds, baseCents);
        allocations = allocation.allocations;
        allocation.contributions.filter((item) => selectedContributionIds.includes(item.id)).forEach((item) => {
          transaction.set(document("temporaryContributions", item.id), { ...item, updatedAt: nowIso() }, { merge: true });
        });
      }
    }

    const historyId = `close_${slug(String(snapshot.periodLabel ?? "period"))}_${Date.now()}`;
    transaction.set(document("periodHistory", historyId), { id: historyId, period: snapshot.periodLabel ?? "Quincena", start: snapshot.periodStart ?? "", end: snapshot.periodEnd ?? "", totalMoneyCents: snapshot.accounts[0]?.balanceCents ?? 0, savingsCents: snapshot.savingsCents, spentCents: snapshot.totalSpentCents, priorExpenseAdjustmentCents: snapshot.priorExpenseAdjustmentCents ?? 0, spentPercent: snapshot.totalBudgetCents ? snapshot.totalSpentCents / snapshot.totalBudgetCents : 0, closingWeekAvailableCents: rollover?.closingWeekCents ?? 0, untransferredCarryoverCents: rollover?.priorCarryoverCents ?? 0, closedAt: nowIso(), origin: "app" });
    transaction.set(document("periods", previousPeriodId), { id: previousPeriodId, name: snapshot.periodLabel ?? "Quincena", startDate: snapshot.periodStart ?? "", endDate: snapshot.periodEnd ?? "", status: "closed", closedAt: nowIso(), origin: "app" }, { merge: true });
    const { week1Cents: week1Budget, week2Cents: week2Budget } = splitPeriodBudgetInHalves(baseCents);
    const ranges = splitPeriodWeekRanges(start, end);
    if (!ranges) throw new Error("No pude dividir las fechas de la nueva quincena.");
    const startDate = new Date(`${ranges.week1.start}T12:00:00`); const week1End = new Date(`${ranges.week1.end}T12:00:00`); const week2Start = new Date(`${ranges.week2.start}T12:00:00`); const endDate = new Date(`${ranges.week2.end}T12:00:00`);
    const funded = mode === 3 ? 0 : baseCents;
    const weeks: FinancialSnapshot["weeks"] = [
      { id: "week1", label: "Semana 1", availableCents: mode === 3 ? 0 : week1Budget, spentCents: 0, budgetCents: week1Budget, fundedCents: mode === 3 ? 0 : week1Budget, pendingFundingCents: mode === 3 ? week1Budget : 0, dateRange: rangeLabel(startDate, week1End), status: "active" },
      { id: "week2", label: "Semana 2", availableCents: mode === 3 ? 0 : week2Budget, spentCents: 0, budgetCents: week2Budget, fundedCents: mode === 3 ? 0 : week2Budget, pendingFundingCents: mode === 3 ? week2Budget : 0, dateRange: rangeLabel(week2Start, endDate), status: "upcoming" },
    ];
    const fundingMode = mode === 1 ? "temporary" : mode === 2 ? "deposited" : "pending_deposit";
    const allocationCreatedAt = nowIso();
    allocations.filter((item) => item.id !== "legacy-temporary-total").forEach((item, index) => {
      const eventId = `event_${operationId}_${index}`;
      const event: TemporaryContributionEvent = {
        id: eventId,
        contributionId: item.id,
        type: "period_allocation",
        amountDeltaCents: -item.usedCents,
        beforeBalanceCents: item.beforeCents,
        afterBalanceCents: item.afterCents,
        descriptionBefore: item.description,
        descriptionAfter: item.description,
        accountDeltaCents: 0,
        note: `Aplicado al presupuesto de ${label}`,
        periodLabel: label,
        createdAt: allocationCreatedAt,
        origin: "app",
      };
      transaction.set(document("temporaryContributionEvents", eventId), event);
    });
    if (rollover) transaction.set(document("periodRollovers", rollover.id), { ...rollover, status: "available", origin: "app" });
    transaction.set(periodReference, { id: periodId, name: label, startDate: start, endDate: end, status: "open", fundingMode, pendingFundingCents: mode === 3 ? baseCents : 0, createdAt: nowIso(), origin: "app" });
    weeks.forEach((week) => transaction.set(document("weeks", `${periodId}_${week.id}`), { ...week, periodId, startDate: week.id === "week1" ? start : isoDateValue(week2Start), endDate: week.id === "week1" ? isoDateValue(week1End) : end, updatedAt: nowIso(), origin: "app" }));
    const next = snapshotForDate({ ...snapshot, periodLabel: label, periodStart: start, periodEnd: end, periodDaysRemaining: 0, periodFundingMode: fundingMode, pendingPeriodFundingCents: mode === 3 ? baseCents : 0, previousPeriodCarryover: rollover, weeks, totalBudgetCents: baseCents, totalSpentCents: 0, priorExpenseAdjustmentCents: 0, categorySpend: [], savingsThisPeriodCents: 0, temporaryContributionCents: mode === 1 ? snapshot.temporaryContributionCents - baseCents : snapshot.temporaryContributionCents, movements: [], source: "firestore" }, dateOnly());
    const result = { ok: true, periodoNuevo: label, inicio: start, fin: end, modo: mode, baseAplicada: money(funded), sobranteAnterior: money(rollover?.availableCents ?? 0), asignaciones: allocations.map((item) => ({ id: item.id, concepto: item.description, usado: money(item.usedCents), saldoDespues: money(item.afterCents) })) };
    transaction.set(operationReference, { id: operationId, kind: "period_create", createdAt: nowIso(), result });
    return { snapshot: next, result };
  });
}

async function fundPeriod(uid: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
  const amountCents = cents(payload.monto);
  const operationId = safeDocumentId(payload.operacionId, "deposito");
  const alreadyIncludedInNu = payload.yaIncluidoEnNu === true;
  return mutateSnapshot(uid, async (snapshot, transaction, document) => {
    const operationReference = document("operations", operationId);
    const operationDocument = await transaction.get(operationReference);
    if (operationDocument.exists()) return { snapshot, result: operationDocument.data().result as Record<string, unknown> };
    const applied = applyPendingPeriodFunding(snapshot, amountCents, !alreadyIncludedInNu);
    const periodId = slug(String(snapshot.periodLabel ?? "period"));
    applied.snapshot.weeks.forEach((week) => transaction.set(document("weeks", `${periodId}_${week.id}`), { ...week, periodId, updatedAt: nowIso(), origin: "app" }, { merge: true }));
    transaction.set(document("periods", periodId), { fundingMode: applied.snapshot.periodFundingMode, pendingFundingCents: applied.snapshot.pendingPeriodFundingCents, updatedAt: nowIso() }, { merge: true });
    transaction.set(document("weeklyAdjustments", operationId), { id: operationId, type: "period_funding", periodId, amountCents, alreadyIncludedInNu, allocations: applied.allocations, createdAt: nowIso(), origin: "app" });
    const result = { ok: true, deposito: money(amountCents), pendiente: money(applied.snapshot.pendingPeriodFundingCents ?? 0), nuAumentado: !alreadyIncludedInNu, asignaciones: applied.allocations.map((item) => ({ semana: item.label, monto: money(item.amountCents) })) };
    transaction.set(operationReference, { id: operationId, kind: "period_funding", createdAt: nowIso(), result });
    return { snapshot: applied.snapshot, result };
  });
}

export async function runFirebaseAction(user: User, kind: ActionJobKind, payload: Record<string, unknown> = {}): Promise<ActionJob> {
  const outcome = await (async () => {
    if (kind === "refresh") return mutateSnapshot(user.uid, (snapshot) => ({ snapshot: snapshotForDate(snapshot), result: { ok: true, calendarioActualizado: true } }));
    if (kind === "update_yield") return updateYield(user.uid, payload);
    if (kind === "week_adjustment") return adjustWeek(user.uid, payload, false);
    if (kind === "week_transfer") return adjustWeek(user.uid, payload, true);
    if (kind === "pending_payment") return pendingPayment(user.uid, payload, false);
    if (kind === "pending_reimbursements") return pendingReimbursementBatch(user.uid, payload);
    if (kind === "pending_reimbursement") return pendingPayment(user.uid, payload, true);
    if (kind === "savings_movement") return savingsMovement(user.uid, payload);
    if (kind === "savings_bucket") return savingsBucket(user.uid, payload);
    if (kind === "savings_transfer") return savingsTransfer(user.uid, payload);
    if (kind === "reconcile_balance") return reconciliation(user.uid, payload);
    if (kind === "card_settings") return cardSettings(user.uid, payload);
    if (kind === "installment_bank_posting") return saveBankPosting(user.uid, payload.input as unknown as BankPostingInput, String(payload.operationId));
    if (kind === "card_summary" || kind === "generate_statement") return cardSummary(user.uid, String(payload.card ?? "AMEX Servicios"), payload.period === "anterior" ? "anterior" : "actual", Number(payload.cutDay ?? 16));
    if (kind === "maintenance_detail" && payload.fullCorrection === true) return readCorrectionDetail(user.uid, String(payload.id));
    if (kind === "maintenance_detail") return maintenanceDetail(user.uid, payload);
    if (kind === "bank_credit_preview") return previewBankCredit(user.uid,payload.input as unknown as BankCreditInput,String(payload.operationId));
    if (kind === "bank_credit") return commitBankCredit(user.uid,payload.input as unknown as BankCreditInput,String(payload.operationId),String(payload.token));
    if (kind === "card_payment_timing_preview") return previewCardPaymentTiming(user.uid,payload.input as unknown as CardPaymentTimingInput,String(payload.operationId));
    if (kind === "card_payment_timing") return commitCardPaymentTiming(user.uid,payload.input as unknown as CardPaymentTimingInput,String(payload.operationId),String(payload.token));
    if (kind === "movement_correction_preview") return previewMovementCorrection(user.uid, payload.input as unknown as CorrectionInput);
    if (kind === "movement_correction") return commitMovementCorrection(user.uid, payload.input as unknown as CorrectionInput, String(payload.operationId), String(payload.token), payload.receipt as import("./types").ReceiptPayload | undefined);
    if (kind === "maintenance_edit") return editMovement(user.uid, payload);
    if (kind === "maintenance_delete") return deleteMovement(user.uid, payload);
    if (kind === "maintenance_reimbursement") return correctReimbursement(user.uid, payload);
    if (kind === "card_payment_states") return cardPaymentStates(user.uid);
    if (kind === "card_payment") return cardPayment(user.uid, payload);
    if (kind === "period_prepare") return preparePeriod(user.uid);
    if (kind === "period_create") return createPeriod(user.uid, payload);
    if (kind === "period_funding") return fundPeriod(user.uid, payload);
    if (kind === "week_to_fund") return weekToFund(user.uid, payload);
    if (kind === "temporary_contribution") return temporaryContribution(user.uid, payload);
    if (kind === "temporary_contribution_manage") return manageTemporaryContribution(user.uid, payload);
    if (kind === "temporary_contribution_transfer") return transferTemporaryContribution(user.uid, payload);
    throw new Error("Esta función todavía no está disponible en Firebase.");
  })();
  const result = "snapshot" in outcome ? outcome.result as Record<string, unknown> : outcome;
  const snapshot = "snapshot" in outcome ? outcome.snapshot as FinancialSnapshot : undefined;
  const now = nowIso();
  return { id: `FIREBASE_${kind}_${crypto.randomUUID()}`, ownerUid: user.uid, kind, status: "synced", payload, result, snapshot, createdAt: now, updatedAt: now, attempts: 1, schemaVersion: 1 };
}
